const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  Package,
  Heart,
  MapPin,
  CreditCard,
  Star,
  Clock,
  Gem,
  ChevronRight,
  Edit3,
  Camera,
  Save,
  Calendar,
  LogOut,
  Settings,
  QrCode,
  ShoppingBag,
  Search,
  Filter,
  Truck,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Trash2,
  Minus,
  Plus,
  CheckSquare,
  Square,
  X,
  Image as ImageIcon,
  Upload,
  Eye,
  Download,
  Store,
  Loader,
  XCircle,
  HelpCircle,
} from 'lucide-react';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

// More reliable placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/300x300/f5f5f5/999999?text=No+Image';

// Helper function to extract numeric ID from order ID
const extractNumericId = (orderId) => {
  if (typeof orderId === 'string') {
    const numericPart = orderId.replace(/[^0-9]/g, '');
    const result = parseInt(numericPart, 10);
    return isNaN(result) ? 0 : result;
  }
  const result = parseInt(orderId, 10);
  return isNaN(result) ? 0 : result;
};

// Helper function to get absolute URL for images
const getAbsoluteUrl = (url) => {
  if (!url) return PLACEHOLDER_IMAGE;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('/uploads/')) return `${window.location.origin}${url}`;
  if (url.startsWith('/api/uploads/')) return `${window.location.origin}${url}`;
  if (url.startsWith('uploads/')) return `${window.location.origin}/${url}`;
  return url;
};

// Helper function to get display status (maps 'Processing' to 'Preparing')
const getDisplayStatus = (status) => {
  if (!status) return 'Pending';
  if (status === 'Processing') return 'Preparing';
  return status;
};

// Helper function to check if order is complete (Delivered or Picked Up)
const isOrderComplete = (status) => {
  if (!status) return false;
  return status === 'Delivered' || status === 'Picked Up';
};

// Helper function to get status style class
const getStatusStyle = (status) => {
  if (!status) return 'bg-orange-50 text-orange-700 border-orange-200';

  const isComplete = isOrderComplete(status);

  if (isComplete) {
    return 'bg-green-100 text-green-700 border-green-200';
  }

  const displayStatus = getDisplayStatus(status);

  const styles = {
    Shipped: 'bg-blue-50 text-blue-700 border-blue-200',
    Preparing: 'bg-purple-50 text-purple-700 border-purple-200',
    Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Ready for Pickup': 'bg-amber-50 text-amber-700 border-amber-200',
    Pending: 'bg-orange-50 text-orange-700 border-orange-200',
  };

  return styles[displayStatus] || 'bg-orange-50 text-orange-700 border-orange-200';
};

// Helper function to get status icon
const getStatusIcon = (status) => {
  if (!status)
    return React.createElement(Clock, {
      size: 14,
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 100 },
    });

  const isComplete = isOrderComplete(status);
  const displayStatus = getDisplayStatus(status);

  if (isComplete) {
    return React.createElement(CheckCircle2, {
      size: 14,
      className: 'text-green-600',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 106 },
    });
  }

  switch (displayStatus) {
    case 'Delivered':
      return React.createElement(CheckCircle2, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 111 },
      });
    case 'Shipped':
      return React.createElement(Truck, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 113 },
      });
    case 'Preparing':
      return React.createElement(Package, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 115 },
      });
    case 'Paid':
      return React.createElement(CheckCircle2, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 117 },
      });
    case 'Ready for Pickup':
      return React.createElement(Store, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 119 },
      });
    default:
      return React.createElement(Clock, {
        size: 14,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 121 },
      });
  }
};

// Helper function to get status display text
const getStatusDisplayText = (status) => {
  if (!status) return 'Pending';
  if (status === 'Delivered' || status === 'Picked Up') return 'Order Complete';
  if (status === 'Processing') return 'Preparing';
  return status;
};

// Tier configuration - realistic points system (100 PHP = 1 point)
const TIER_CONFIG = {
  Sapphire: {
    minPoints: 0,
    maxPoints: 4999,
    nextTier: 'Ruby',
    color: 'from-blue-600 to-blue-400',
    badgeColor: 'bg-blue-600',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    bgLight: 'bg-blue-50',
    icon: '💎',
    benefits: ['Welcome points', 'Basic concierge', 'Birthday rewards'],
  },
  Ruby: {
    minPoints: 5000,
    maxPoints: 14999,
    nextTier: 'Emerald',
    color: 'from-red-600 to-red-400',
    badgeColor: 'bg-red-600',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    bgLight: 'bg-red-50',
    icon: '❤️',
    benefits: ['5% discount', 'Priority support', 'Early access sales', 'Exclusive events'],
  },
  Emerald: {
    minPoints: 15000,
    maxPoints: 49999,
    nextTier: 'Diamond',
    color: 'from-emerald-600 to-emerald-400',
    badgeColor: 'bg-emerald-600',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    bgLight: 'bg-emerald-50',
    icon: '✨',
    benefits: ['10% discount', 'Free shipping', 'Private viewings', 'Personal shopper'],
  },
  Diamond: {
    minPoints: 50000,
    maxPoints: Infinity,
    nextTier: null,
    color: 'from-cyan-500 to-blue-500',
    badgeColor: 'bg-gradient-to-r from-cyan-500 to-blue-500',
    textColor: 'text-cyan-600',
    borderColor: 'border-cyan-200',
    bgLight: 'bg-cyan-50',
    icon: '👑',
    benefits: [
      '15% discount',
      'VIP events',
      'Personal stylist',
      'Complimentary insurance',
      'Priority reservations',
    ],
  },
};

// Calculate points from total spent (100 PHP = 1 point)
const calculatePointsFromSpent = (totalSpent) => {
  return Math.floor(totalSpent / 100);
};

// Get tier based on points
const getTierFromPoints = (points) => {
  if (points >= 50000) return 'Diamond';
  if (points >= 15000) return 'Emerald';
  if (points >= 5000) return 'Ruby';
  return 'Sapphire';
};

// Get tier details
const getTierDetails = (points) => {
  const tierName = getTierFromPoints(points);
  const config = TIER_CONFIG[tierName];
  const nextTierConfig = config.nextTier ? TIER_CONFIG[config.nextTier] : null;

  const pointsToNext = nextTierConfig ? nextTierConfig.minPoints - points : 0;
  const spendToNext = pointsToNext * 100;
  const progress = nextTierConfig
    ? ((points - config.minPoints) / (nextTierConfig.minPoints - config.minPoints)) * 100
    : 100;

  return {
    tierName,
    config,
    nextTierName: config.nextTier,
    pointsToNext,
    spendToNext,
    progress: Math.min(100, Math.max(0, progress)),
  };
};

const MemberProfile = ({
  user,
  onNavigate,
  onLogout,
  onUpdateUser,
  initialTab = 'overview',
  wishlist = [],
  onUpdateWishlistQuantity,
  onToggleWishlistSelection,
  onToggleAllWishlistSelection,
  onRemoveFromWishlist,
  onMoveToCart,
  onSelectProduct,
  onRefreshWishlist,
  orders = [],
  onUpdateOrder,
  onRefreshOrders,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Tier Info Modal State
  const [showTierInfoModal, setShowTierInfoModal] = useState(false);

  // Order History State
  const [orderFilter, setOrderFilter] = useState('All');
  const [orderSearch, setOrderSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Tracking Popup State
  const [trackingModalOrder, setTrackingModalOrder] = useState(null);
  const [orderTimeline, setOrderTimeline] = useState([]);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [defaultBank, setDefaultBank] = useState(null);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Payment Submission Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    orderId: '',
    transactionRef: '',
    proofPreview: null,
    isUploading: false,
  });

  // Profile Photo Upload State - INCREASED to 20MB
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState(null);

  // Image error states
  const [imageErrors, setImageErrors] = useState({});
  const [wishlistImageErrors, setWishlistImageErrors] = useState({});

  // Profile Data State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: '',
    address: '',
    city: '',
    postalCode: '',
    avatar: PLACEHOLDER_IMAGE,
  });

  // Mock Data (will be replaced with real data from API)
  const [memberSince, setMemberSince] = useState('October 2023');
  const [tier, setTier] = useState('Sapphire');
  const [points, setPoints] = useState(1250);
  const nextTierPoints = 5000;

  // Calculate real points from orders
  const totalSpent = orders.reduce(
    (sum, order) => sum + (order.totalAmount || order.total || 0),
    0
  );
  const calculatedPoints = calculatePointsFromSpent(totalSpent);
  const tierDetails = getTierDetails(calculatedPoints);

  // Member since date
  const memberSinceDate = _optionalChain([user, 'optionalAccess', (_) => _.created_at])
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : orders.length > 0 &&
        _optionalChain([orders, 'access', (_2) => _2[0], 'optionalAccess', (_3) => _3.orderDate])
      ? new Date(orders[0].orderDate).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        })
      : 'March 2026';

  // Member ID
  const memberId = _optionalChain([user, 'optionalAccess', (_4) => _4.id])
    ? `SG-${new Date().getFullYear()}-${String(user.id).padStart(4, '0')}`
    : 'SG-2026-0001';

  // Lightbox functions
  const openLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  // Load user data when component mounts or user changes
  useEffect(() => {
    // Fetch default bank details for payment proof section
    fetch(`${API_BASE_URL}/bank-details/get`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.banks?.length > 0) {
          setDefaultBank(d.banks.find((b) => b.is_default) || d.banks[0]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (_optionalChain([user, 'optionalAccess', (_5) => _5.id])) {
      loadUserProfile();
    } else {
      setProfileData({
        firstName:
          _optionalChain([
            user,
            'optionalAccess',
            (_6) => _6.name,
            'optionalAccess',
            (_7) => _7.split,
            'call',
            (_8) => _8(' '),
            'access',
            (_9) => _9[0],
          ]) || 'Guest',
        lastName:
          _optionalChain([
            user,
            'optionalAccess',
            (_10) => _10.name,
            'optionalAccess',
            (_11) => _11.split,
            'call',
            (_12) => _12(' '),
            'access',
            (_13) => _13.slice,
            'call',
            (_14) => _14(1),
            'access',
            (_15) => _15.join,
            'call',
            (_16) => _16(' '),
          ]) || 'User',
        email:
          _optionalChain([user, 'optionalAccess', (_17) => _17.email]) || 'member@sparklinggems.ph',
        phone: _optionalChain([user, 'optionalAccess', (_18) => _18.phone]) || '+63 900 000 0000',
        birthday: '',
        address: 'Metro Manila, Philippines',
        city: 'Makati',
        postalCode: '1200',
        avatar: _optionalChain([user, 'optionalAccess', (_19) => _19.avatar]) || PLACEHOLDER_IMAGE,
      });
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!_optionalChain([user, 'optionalAccess', (_20) => _20.id])) {
      setProfileLoadError('User ID not available');
      return;
    }

    setIsLoadingProfile(true);
    setProfileLoadError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/users/get-profile?user_id=${user.id}`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.user) {
        const avatarUrl = getAbsoluteUrl(data.user.avatar);

        setProfileData({
          firstName: data.user.firstName || data.user.first_name || '',
          lastName: data.user.lastName || data.user.last_name || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          birthday: data.user.birthday || '',
          address: data.user.address || '',
          city: data.user.city || '',
          postalCode: data.user.postalCode || data.user.postal_code || '',
          avatar: avatarUrl,
        });

        if (data.user.membership_tier) setTier(data.user.membership_tier);
        if (data.user.points) setPoints(data.user.points);
        if (data.user.created_at) {
          const date = new Date(data.user.created_at);
          setMemberSince(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }
      } else {
        setProfileData({
          firstName:
            _optionalChain([
              user,
              'optionalAccess',
              (_21) => _21.name,
              'optionalAccess',
              (_22) => _22.split,
              'call',
              (_23) => _23(' '),
              'access',
              (_24) => _24[0],
            ]) || 'Guest',
          lastName:
            _optionalChain([
              user,
              'optionalAccess',
              (_25) => _25.name,
              'optionalAccess',
              (_26) => _26.split,
              'call',
              (_27) => _27(' '),
              'access',
              (_28) => _28.slice,
              'call',
              (_29) => _29(1),
              'access',
              (_30) => _30.join,
              'call',
              (_31) => _31(' '),
            ]) || 'User',
          email:
            _optionalChain([user, 'optionalAccess', (_32) => _32.email]) ||
            'member@sparklinggems.ph',
          phone: _optionalChain([user, 'optionalAccess', (_33) => _33.phone]) || '+63 900 000 0000',
          birthday: '',
          address: 'Metro Manila, Philippines',
          city: 'Makati',
          postalCode: '1200',
          avatar:
            getAbsoluteUrl(_optionalChain([user, 'optionalAccess', (_34) => _34.avatar])) ||
            PLACEHOLDER_IMAGE,
        });
      }
    } catch (error) {
      setProfileLoadError(error instanceof Error ? error.message : 'Failed to load profile');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Update URL when tab changes
  useEffect(() => {
    const tabMap = {
      overview: 'profile',
      orders: 'orders',
      wishlist: 'wishlist',
      settings: 'settings',
      'payment-proof': 'upload-proof',
    };

    const path = tabMap[activeTab] || 'profile';
    window.history.pushState(null, '', `/${path}`);
  }, [activeTab]);

  // Reload wishlist from DB whenever the wishlist tab becomes active (keeps in sync with popup panel)
  useEffect(() => {
    if (activeTab === 'wishlist' && onRefreshWishlist) {
      onRefreshWishlist();
    }
  }, [activeTab]);

  // Fetch timeline when tracking modal opens
  useEffect(() => {
    if (trackingModalOrder) {
      fetchTimeline(trackingModalOrder.id);
    }
  }, [trackingModalOrder]);

  const fetchTimeline = async (orderId) => {
    setIsLoadingTimeline(true);
    setOrderDetails(null);
    setTimelineError(null);
    try {
      const numericId = extractNumericId(orderId);

      if (!numericId) {
        setOrderTimeline([]);
        setIsLoadingTimeline(false);
        return;
      }

      const timestamp = new Date().getTime();
      const response = await fetch(
        `${API_BASE_URL}/orders/get-timeline?order_id=${numericId}&_=${timestamp}`,
        { headers: authHeaders() }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const sortedTimeline = (data.timeline || []).sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setOrderTimeline(sortedTimeline);

        if (data.order) {
          setOrderDetails(data.order);
        }
      } else {
        setOrderTimeline([]);
        setTimelineError('Could not load order timeline. Please try again.');
      }
    } catch (error) {
      setOrderTimeline([]);
      setTimelineError('Connection error. Please check your internet and try again.');
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if ((activeTab === 'orders' || activeTab === 'payment-proof') && onRefreshOrders) {
      onRefreshOrders();
    }
  }, [activeTab]);

  useEffect(() => {
    if (orders.length > 0) {
    }
  }, [orders]);

  const pendingOrders = orders.filter(
    (o) => o && (o.status === 'Pending' || o.status === 'Rejected')
  );

  // Handle profile save with API call
  const handleSave = async (e) => {
    e.preventDefault();

    if (!_optionalChain([user, 'optionalAccess', (_35) => _35.id])) {
      showToast('User not logged in', 'error');
      return;
    }

    setIsUploadingPhoto(true);
    setSaveSuccess(false);

    try {
      const payload = {
        user_id: user.id,
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        city: profileData.city || '',
        postalCode: profileData.postalCode || '',
      };

      const response = await fetch(`${API_BASE_URL}/users/update-profile`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Server returned invalid response');
      }

      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        showToast('Profile updated successfully!');

        if (onUpdateUser && user) {
          const updatedUser = {
            ...user,
            name: `${profileData.firstName} ${profileData.lastName}`.trim(),
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            email: profileData.email,
            phone: profileData.phone,
          };
          onUpdateUser(updatedUser);
        }

        await loadUserProfile();
        setIsEditing(false);
      } else {
        showToast('Failed to update profile: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Error saving profile. Please try again.',
        'error'
      );
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Profile Photo Upload Handlers - UPDATED to 20MB
  const handlePhotoClick = () => {
    setShowPhotoUploadModal(true);
  };

  const handlePhotoSelect = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_36) => _36.target,
      'access',
      (_37) => _37.files,
      'optionalAccess',
      (_38) => _38[0],
    ]);
    if (file) {
      // Increased file size limit to 20MB
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        showToast('File too large. Maximum 20MB allowed.', 'error');
        return;
      }

      if (!file.type.startsWith('image/')) {
        showToast('Please upload an image file.', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto || !_optionalChain([user, 'optionalAccess', (_39) => _39.id])) {
      showToast('No photo selected or user not logged in', 'error');
      return;
    }

    setIsUploadingPhoto(true);

    try {
      // selectedPhoto is already a base64 data URL — send it directly as JSON
      const uploadResponse = await fetch(`${API_BASE_URL}/users/update-avatar`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: user.id, avatar: selectedPhoto }),
      });

      if (!uploadResponse.ok) {
        if (uploadResponse.status === 413) {
          throw new Error('File too large. Please use a smaller image (max 20MB).');
        }
        const errorText = await uploadResponse.text();
        throw new Error(`HTTP error! status: ${uploadResponse.status}`);
      }

      const result = await uploadResponse.json();

      if (result.success) {
        const avatarUrl = selectedPhoto;
        setProfileData((prev) => ({ ...prev, avatar: avatarUrl }));
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        showToast('Profile photo updated successfully!');

        if (onUpdateUser && user) {
          const updatedUser = {
            ...user,
            avatar: avatarUrl,
          };
          onUpdateUser(updatedUser);
        }

        setSelectedPhoto(null);
        setShowPhotoUploadModal(false);
        await loadUserProfile();
      } else {
        showToast('Failed to upload photo: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Error uploading photo. Please try again.',
        'error'
      );
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const triggerFileInput = () => {
    _optionalChain([
      fileInputRef,
      'access',
      (_40) => _40.current,
      'optionalAccess',
      (_41) => _41.click,
      'call',
      (_42) => _42(),
    ]);
  };

  // ============================================================
  // Payment Modal Handlers - Direct API Upload
  // ============================================================
  const openPaymentModal = () => {
    setPaymentForm({
      orderId:
        pendingOrders.length > 0
          ? _optionalChain([
              pendingOrders,
              'access',
              (_43) => _43[0],
              'optionalAccess',
              (_44) => _44.id,
            ]) || ''
          : '',
      transactionRef: '',
      proofPreview: null,
      isUploading: false,
    });
    setShowPaymentModal(true);
  };

  const handleModalFileChange = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_45) => _45.target,
      'access',
      (_46) => _46.files,
      'optionalAccess',
      (_47) => _47[0],
    ]);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('File too large. Maximum 5MB allowed.', 'error');
        return;
      }

      if (!file.type.startsWith('image/')) {
        showToast('Please upload an image file.', 'error');
        return;
      }

      const url = URL.createObjectURL(file);
      setPaymentForm((prev) => ({ ...prev, proofPreview: url }));
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.orderId || !paymentForm.proofPreview || !paymentForm.transactionRef) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    setPaymentForm((prev) => ({ ...prev, isUploading: true }));

    try {
      const response = await fetch(paymentForm.proofPreview);
      const blob = await response.blob();

      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const paymentProofBase64 = await base64Promise;
      const numericOrderId = extractNumericId(paymentForm.orderId);
      const userId = _optionalChain([user, 'optionalAccess', (_48) => _48.id]) || null;

      const uploadResponse = await fetch(`${API_BASE_URL}/orders/upload-proof`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          order_id: numericOrderId,
          payment_proof: paymentProofBase64,
          transaction_id: paymentForm.transactionRef,
          user_id: userId,
        }),
      });

      const result = await uploadResponse.json();

      if (result.success) {
        showToast('Payment proof submitted successfully! Our team will verify it shortly.');
        setShowPaymentModal(false);

        if (onUpdateOrder && paymentForm.orderId) {
          onUpdateOrder(paymentForm.orderId, {
            paymentProof: paymentProofBase64,
            transactionId: paymentForm.transactionRef,
          });
        }

        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        showToast('Failed to upload proof: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Failed to upload proof. Please try again.', 'error');
    } finally {
      setPaymentForm((prev) => ({ ...prev, isUploading: false }));
    }
  };

  const handleProofUpload = async (e, orderId) => {
    const file = _optionalChain([
      e,
      'access',
      (_49) => _49.target,
      'access',
      (_50) => _50.files,
      'optionalAccess',
      (_51) => _51[0],
    ]);
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('File too large. Maximum 5MB allowed.', 'error');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file.', 'error');
      return;
    }

    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const paymentProofBase64 = await base64Promise;
      const numericOrderId = extractNumericId(orderId);
      const userId = _optionalChain([user, 'optionalAccess', (_52) => _52.id]) || null;
      const transactionId = `TXN-${Date.now().toString().slice(-8)}-${Math.floor(Math.random() * 1000)}`;

      const uploadResponse = await fetch(`${API_BASE_URL}/orders/upload-proof`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          order_id: numericOrderId,
          payment_proof: paymentProofBase64,
          transaction_id: transactionId,
          user_id: userId,
        }),
      });

      const result = await uploadResponse.json();

      if (result.success) {
        showToast('Receipt uploaded successfully. Status will be updated after admin review.');

        if (onUpdateOrder) {
          onUpdateOrder(orderId, {
            paymentProof: paymentProofBase64,
            transactionId: transactionId,
          });
        }

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        showToast('Failed to upload proof: ' + (result.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Failed to upload proof. Please try again.', 'error');
    }

    e.target.value = '';
  };

  // Filter Orders
  const filteredOrders = orders.filter((order) => {
    if (!order) return false;

    const orderIdStr = order.id ? String(order.id).toLowerCase() : '';
    const searchLower = orderSearch.toLowerCase();

    const matchesId = orderIdStr.includes(searchLower);

    const matchesItem =
      order.items &&
      order.items.some(
        (item) => item && item.name && item.name.toLowerCase().includes(searchLower)
      );

    const matchesSearch = matchesId || matchesItem;
    const matchesFilter = orderFilter === 'All' || (order.status && order.status === orderFilter);

    return matchesSearch && matchesFilter;
  });

  const toggleOrderExpand = (id) => {
    setExpandedOrder(expandedOrder === id ? null : id);
  };

  const downloadInvoice = (order) => {
    try {
      const orderAny = order;
      const doc = new jsPDF();

      doc.setFontSize(22);
      doc.setFont('times', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('Sparkling Gems', 105, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('INVOICE', 105, 30, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order #: ${orderAny.order_number || order.id}`, 20, 45);

      const orderDate = order.orderDate
        ? new Date(order.orderDate).toLocaleString('en-PH')
        : order.date
          ? new Date(order.date).toLocaleString('en-PH')
          : new Date().toLocaleString('en-PH');
      doc.text(`Date: ${orderDate}`, 20, 52);

      doc.setFontSize(11);
      doc.text('Bill To:', 20, 65);
      doc.setFontSize(10);

      const customerName =
        order.customerName ||
        orderAny.customer_name ||
        `${profileData.firstName} ${profileData.lastName}`;
      doc.text(customerName, 20, 72);

      const customerEmail = orderAny.customer_email || profileData.email;
      doc.text(customerEmail, 20, 79);

      const customerPhone = orderAny.customer_phone || profileData.phone;
      doc.text(customerPhone, 20, 86);

      const isPickup = orderAny.deliveryType === 'pickup' || orderAny.delivery_type === 'pickup';

      doc.text('Ship To:', 120, 65);
      let shippingAddress = '';
      if (isPickup) {
        shippingAddress =
          'Store Pickup - Sparkling Gems Jewelry Shop\n2F, San Antonio Building, Corumi, Quezon City';
      } else {
        shippingAddress =
          order.shippingAddress ||
          orderAny.shipping_address ||
          `${profileData.address}, ${profileData.city} ${profileData.postalCode}`;
      }
      doc.text(shippingAddress, 120, 72, { maxWidth: 70 });

      const tableColumn = ['Item', 'Qty', 'Price', 'Total'];
      const tableRows = [];

      if (order.items && order.items.length > 0) {
        order.items.forEach((item) => {
          const itemData = [
            item.name || 'Product',
            _optionalChain([
              item,
              'access',
              (_53) => _53.quantity,
              'optionalAccess',
              (_54) => _54.toString,
              'call',
              (_55) => _55(),
            ]) || '1',
            `PHP ${(item.price || 0).toLocaleString('en-US')}`,
            `PHP ${((item.price || 0) * (item.quantity || 1)).toLocaleString('en-US')}`,
          ];
          tableRows.push(itemData);
        });
      } else {
        tableRows.push(['No items', '-', '-', 'PHP 0']);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 100,
        theme: 'striped',
        headStyles: { fillColor: [180, 148, 92] },
      });

      const finalY =
        _optionalChain([
          doc,
          'access',
          (_56) => _56.lastAutoTable,
          'optionalAccess',
          (_57) => _57.finalY,
        ]) + 10 || 150;

      const subtotal = order.subtotal || 0;
      const total = order.total || order.totalAmount || 0;

      doc.text(`Subtotal: PHP ${subtotal.toLocaleString('en-US')}`, 140, finalY);
      doc.text(`Shipping: FREE`, 140, finalY + 7);
      doc.setFontSize(12);
      doc.setFont('times', 'bold');
      doc.text(`Total: PHP ${total.toLocaleString('en-US')}`, 140, finalY + 17);

      doc.setFontSize(10);
      doc.setFont('times', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Payment Method: ${order.paymentMethod || orderAny.payment_method || 'Bank Transfer'}`,
        20,
        finalY + 10
      );

      const transactionId = order.transactionId || orderAny.transaction_id || 'N/A';
      doc.text(`Transaction ID: ${transactionId}`, 20, finalY + 17);
      doc.text(`Status: ${getStatusDisplayText(order.status)}`, 20, finalY + 24);

      doc.save(`invoice-${order.id}.pdf`);
      showToast('Invoice downloaded successfully!');
    } catch (error) {
      showToast('Failed to generate invoice. Please try again.', 'error');
    }
  };

  const selectedWishlistItems = wishlist.filter((item) => item && item.selected);
  const isAllSelected = wishlist.length > 0 && selectedWishlistItems.length === wishlist.length;

  const handleBulkDelete = () => {
    if (onRemoveFromWishlist) {
      onRemoveFromWishlist(selectedWishlistItems.map((i) => i.id));
    }
  };

  const handleBulkMoveToCart = () => {
    if (onMoveToCart) {
      onMoveToCart(selectedWishlistItems.map((i) => i.id));
    }
  };

  const formatNumber = (value, defaultValue = '0') => {
    if (value === undefined || value === null || isNaN(value)) return defaultValue;
    try {
      return value.toLocaleString();
    } catch (e) {
      return defaultValue;
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '₱0';
    try {
      return `₱${value.toLocaleString()}`;
    } catch (e) {
      return '₱0';
    }
  };

  const getProductImage = (item, itemId) => {
    if (!item) return PLACEHOLDER_IMAGE;
    if (itemId && imageErrors[`${itemId}`]) {
      return PLACEHOLDER_IMAGE;
    }

    const possibleImageFields = [
      'image',
      'image_url',
      'mainImage',
      'productImage',
      'img',
      'photo',
      'product_image',
    ];
    for (const field of possibleImageFields) {
      if (item[field] && typeof item[field] === 'string' && item[field].trim() !== '') {
        return getAbsoluteUrl(item[field]);
      }
    }

    return PLACEHOLDER_IMAGE;
  };

  const handleImageError = (itemId) => {
    setImageErrors((prev) => ({ ...prev, [itemId]: true }));
  };

  const handleWishlistImageError = (itemId) => {
    setWishlistImageErrors((prev) => ({ ...prev, [itemId]: true }));
  };

  const createProductFromItem = (item) => {
    return {
      id: item.product_id || item.id || 0,
      name: item.name || 'Product',
      description: item.description || item.name || 'Product',
      price: item.price || 0,
      image: getProductImage(item, item.id),
      category: item.category || 'jewelry',
      isNew: false,
      isBestSeller: false,
      condition: 'Brand New',
      weight: item.weight || '',
      karat: item.karat || '',
      goldType: item.goldType || item.gold_type || '',
      stock: item.stock || 0,
    };
  };

  const getTimelineImageUrl = (event) => {
    if (event.image_url) {
      return getAbsoluteUrl(event.image_url);
    }

    if (event.id && event.has_image) {
      return `${API_BASE_URL}/get-image?id=${event.id}`;
    }

    if (event.proof_image && !event.proof_image.startsWith('blob:')) {
      return getAbsoluteUrl(event.proof_image);
    }

    return PLACEHOLDER_IMAGE;
  };

  // Tier Info Modal Component
  const TierInfoModal = () => {
    const tiers = [
      {
        name: 'Sapphire',
        icon: '💎',
        minPoints: 0,
        maxPoints: 4999,
        minSpend: '₱0',
        maxSpend: '₱499,900',
        benefits: TIER_CONFIG.Sapphire.benefits,
      },
      {
        name: 'Ruby',
        icon: '❤️',
        minPoints: 5000,
        maxPoints: 14999,
        minSpend: '₱500,000',
        maxSpend: '₱1,499,900',
        benefits: TIER_CONFIG.Ruby.benefits,
      },
      {
        name: 'Emerald',
        icon: '✨',
        minPoints: 15000,
        maxPoints: 49999,
        minSpend: '₱1,500,000',
        maxSpend: '₱4,999,900',
        benefits: TIER_CONFIG.Emerald.benefits,
      },
      {
        name: 'Diamond',
        icon: '👑',
        minPoints: 50000,
        maxPoints: '∞',
        minSpend: '₱5,000,000',
        maxSpend: '∞',
        benefits: TIER_CONFIG.Diamond.benefits,
      },
    ];

    return React.createElement(
      'div',
      {
        className: 'fixed inset-0 z-[500] flex items-center justify-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 1041 },
      },
      React.createElement('div', {
        className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
        onClick: () => setShowTierInfoModal(false),
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 1042 },
      }),
      React.createElement(
        'div',
        {
          className:
            'relative bg-white w-full max-w-4xl rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[501] max-h-[90vh] overflow-y-auto',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1043 },
        },
        React.createElement(
          'div',
          {
            className: 'bg-stone-900 text-white p-6 flex justify-between items-center sticky top-0',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1044 },
          },
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1045 } },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-2xl',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1046 },
              },
              'Loyalty Tiers & Benefits'
            ),
            React.createElement(
              'p',
              {
                className: 'text-xs text-stone-400 mt-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1047 },
              },
              'Earn points and unlock exclusive privileges'
            )
          ),
          React.createElement(
            'button',
            {
              onClick: () => setShowTierInfoModal(false),
              className: 'text-stone-400 hover:text-white transition-colors',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1049 },
            },
            React.createElement(X, {
              size: 24,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1050 },
            })
          )
        ),

        React.createElement(
          'div',
          {
            className: 'p-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1054 },
          },
          React.createElement(
            'div',
            {
              className: 'mb-8 bg-gold-50 p-6 rounded-sm border border-gold-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1055 },
            },
            React.createElement(
              'div',
              {
                className: 'flex items-center gap-3 mb-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1056 },
              },
              React.createElement(Star, {
                size: 24,
                className: 'text-gold-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1057 },
              }),
              React.createElement(
                'h4',
                {
                  className: 'font-bold text-stone-900 text-lg',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1058 },
                },
                'How to Earn Points'
              )
            ),
            React.createElement(
              'p',
              {
                className: 'text-stone-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1060 },
              },
              'Earn ',
              React.createElement(
                'strong',
                {
                  className: 'text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1060 },
                },
                '1 point for every ₱100 spent'
              ),
              ' on any purchase. Points never expire and can be redeemed for exclusive rewards, discounts, and special experiences.'
            )
          ),

          React.createElement(
            'div',
            {
              className: 'overflow-x-auto',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1063 },
            },
            React.createElement(
              'table',
              {
                className: 'w-full border-collapse',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1064 },
              },
              React.createElement(
                'thead',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1065 } },
                React.createElement(
                  'tr',
                  {
                    className: 'bg-stone-100',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1066 },
                  },
                  React.createElement(
                    'th',
                    {
                      className:
                        'px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1067 },
                    },
                    'Tier'
                  ),
                  React.createElement(
                    'th',
                    {
                      className:
                        'px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1068 },
                    },
                    'Points Required'
                  ),
                  React.createElement(
                    'th',
                    {
                      className:
                        'px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1069 },
                    },
                    'Spend Required'
                  ),
                  React.createElement(
                    'th',
                    {
                      className:
                        'px-4 py-3 text-left text-xs uppercase tracking-widest text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1070 },
                    },
                    'Benefits'
                  )
                )
              ),
              React.createElement(
                'tbody',
                {
                  className: 'divide-y divide-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1073 },
                },
                tiers.map((tier, idx) =>
                  React.createElement(
                    'tr',
                    {
                      key: idx,
                      className: 'hover:bg-stone-50 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1075 },
                    },
                    React.createElement(
                      'td',
                      {
                        className: 'px-4 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1076 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1077 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: 'text-xl',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1078 },
                          },
                          tier.icon
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'font-bold text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1079 },
                          },
                          tier.name
                        )
                      )
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-4 py-4 text-stone-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1082 },
                      },
                      typeof tier.minPoints === 'number'
                        ? tier.minPoints.toLocaleString()
                        : tier.minPoints,
                      ' - ',
                      typeof tier.maxPoints === 'number'
                        ? tier.maxPoints.toLocaleString()
                        : tier.maxPoints,
                      ' pts'
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-4 py-4 text-stone-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1085 },
                      },
                      tier.minSpend,
                      ' - ',
                      tier.maxSpend
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-4 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1088 },
                      },
                      React.createElement(
                        'ul',
                        {
                          className: 'space-y-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1089 },
                        },
                        tier.benefits.map((benefit, i) =>
                          React.createElement(
                            'li',
                            {
                              key: i,
                              className: 'text-xs text-stone-600 flex items-center gap-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1091 },
                            },
                            React.createElement(CheckCircle2, {
                              size: 10,
                              className: 'text-green-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1092 },
                            }),
                            benefit
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          ),

          React.createElement(
            'div',
            {
              className: 'mt-8 p-6 bg-stone-50 rounded-sm border border-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1104 },
            },
            React.createElement(
              'h4',
              {
                className: 'font-bold text-stone-900 mb-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1105 },
              },
              'Your Current Progress'
            ),
            React.createElement(
              'div',
              {
                className: 'flex items-center justify-between mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1106 },
              },
              React.createElement(
                'span',
                {
                  className: 'text-sm font-medium',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1107 },
                },
                tierDetails.tierName
              ),
              React.createElement(
                'span',
                {
                  className: 'text-sm text-stone-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1108 },
                },
                calculatedPoints.toLocaleString(),
                ' points'
              )
            ),
            React.createElement(
              'div',
              {
                className: 'h-2 bg-stone-200 rounded-full overflow-hidden',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1110 },
              },
              React.createElement('div', {
                className: `h-full bg-gradient-to-r ${tierDetails.config.color} rounded-full transition-all duration-500`,
                style: { width: `${tierDetails.progress}%` },
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1111 },
              })
            ),
            tierDetails.nextTierName &&
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-500 mt-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1117 },
                },
                '✨ ',
                tierDetails.pointsToNext.toLocaleString(),
                ' more points needed to reach ',
                tierDetails.nextTierName
              )
          )
        )
      )
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return React.createElement(
          'div',
          {
            className: 'space-y-8 animate-fade-in',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1132 },
          },
          isLoadingProfile
            ? React.createElement(
                'div',
                {
                  className: 'flex justify-center py-12',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1134 },
                },
                React.createElement(Loader, {
                  size: 32,
                  className: 'animate-spin text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1135 },
                })
              )
            : profileLoadError
              ? React.createElement(
                  'div',
                  {
                    className:
                      'bg-red-50 border border-red-200 text-red-700 p-6 rounded-sm text-center',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1138 },
                  },
                  React.createElement(
                    'p',
                    {
                      className: 'mb-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1139 },
                    },
                    profileLoadError
                  ),
                  React.createElement(
                    'button',
                    {
                      onClick: loadUserProfile,
                      className:
                        'px-4 py-2 bg-red-600 text-white text-xs uppercase tracking-wider hover:bg-red-700 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1140 },
                    },
                    'Retry'
                  )
                )
              : React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(
                    'div',
                    {
                      className: 'grid grid-cols-1 md:grid-cols-3 gap-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1149 },
                    },
                    React.createElement(
                      'div',
                      {
                        className:
                          'bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1150 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center justify-between mb-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1151 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: `p-3 rounded-full ${tierDetails.config.bgLight}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1152 },
                          },
                          React.createElement(Package, {
                            size: 20,
                            className: tierDetails.config.textColor,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1153 },
                          })
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'text-2xl font-serif text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1155 },
                          },
                          orders.length
                        )
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1157 },
                        },
                        'Total Orders'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-400 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1158 },
                        },
                        'Lifetime purchases'
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className:
                          'bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1161 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center justify-between mb-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1162 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: `p-3 rounded-full ${tierDetails.config.bgLight}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1163 },
                          },
                          React.createElement(Heart, {
                            size: 20,
                            className: tierDetails.config.textColor,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1164 },
                          })
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'text-2xl font-serif text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1166 },
                          },
                          wishlist.length
                        )
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1168 },
                        },
                        'Wishlist Items'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-400 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1169 },
                        },
                        'Saved for later'
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className:
                          'bg-white p-6 border border-stone-200 rounded-sm shadow-sm hover:shadow-md transition-all',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1172 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center justify-between mb-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1173 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: `p-3 rounded-full ${tierDetails.config.bgLight}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1174 },
                          },
                          React.createElement(Star, {
                            size: 20,
                            className: tierDetails.config.textColor,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1175 },
                          })
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'text-2xl font-serif text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1177 },
                          },
                          calculatedPoints.toLocaleString()
                        )
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1179 },
                        },
                        'Loyalty Points'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-400 mt-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1180 },
                        },
                        '100 PHP = 1 point'
                      )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'grid grid-cols-1 lg:grid-cols-2 gap-8',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1184 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'space-y-6',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1185 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center justify-between',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1186 },
                        },
                        React.createElement(
                          'div',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1187 } },
                          React.createElement(
                            'h2',
                            {
                              className: 'font-serif text-3xl text-stone-900',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1188 },
                            },
                            'Welcome, ',
                            profileData.firstName || 'Member',
                            '.'
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-stone-500 font-light text-sm mt-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1189 },
                            },
                            'Manage your collection and concierge requests.'
                          )
                        ),
                        React.createElement(
                          'button',
                          {
                            onClick: () => setShowTierInfoModal(true),
                            className:
                              'w-8 h-8 rounded-full bg-stone-100 hover:bg-gold-100 text-stone-500 hover:text-gold-600 transition-all flex items-center justify-center group',
                            title: 'View tier information',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1191 },
                          },

                          React.createElement(HelpCircle, {
                            size: 18,
                            className: 'group-hover:scale-110 transition-transform',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1196 },
                          })
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: `bg-gradient-to-r ${tierDetails.config.color} p-6 rounded-sm shadow-lg text-white`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1200 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex justify-between items-start mb-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1201 },
                          },
                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1202 },
                            },
                            React.createElement(
                              'span',
                              {
                                className: 'text-3xl mr-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1203 },
                              },
                              tierDetails.config.icon
                            ),
                            React.createElement(
                              'span',
                              {
                                className: 'text-xs uppercase tracking-widest opacity-80',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1204 },
                              },
                              'Current Tier'
                            ),
                            React.createElement(
                              'h3',
                              {
                                className: 'font-serif text-3xl mt-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1205 },
                              },
                              tierDetails.tierName
                            )
                          ),
                          React.createElement(
                            'div',
                            {
                              className: 'text-right',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1207 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-2xl font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1208 },
                              },
                              calculatedPoints.toLocaleString()
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs opacity-80',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1209 },
                              },
                              'Loyalty Points'
                            )
                          )
                        ),

                        tierDetails.nextTierName &&
                          React.createElement(
                            'div',
                            {
                              className: 'mt-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1214 },
                            },
                            React.createElement(
                              'div',
                              {
                                className: 'flex justify-between text-xs mb-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1215 },
                              },
                              React.createElement(
                                'span',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1216 },
                                },
                                calculatedPoints.toLocaleString(),
                                ' pts'
                              ),
                              React.createElement(
                                'span',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1217 },
                                },
                                tierDetails.nextTierName,
                                ': ',
                                tierDetails.pointsToNext.toLocaleString(),
                                ' more pts'
                              ),
                              React.createElement(
                                'span',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1218 },
                                },
                                TIER_CONFIG[tierDetails.nextTierName].minPoints.toLocaleString(),
                                ' pts'
                              )
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'h-2 bg-white/30 rounded-full overflow-hidden',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1220 },
                              },
                              React.createElement('div', {
                                className:
                                  'h-full bg-white rounded-full transition-all duration-1000 ease-out',
                                style: { width: `${tierDetails.progress}%` },
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1221 },
                              })
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs mt-3 opacity-90',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1226 },
                              },
                              '✨ Spend ₱ ',
                              tierDetails.spendToNext.toLocaleString(),
                              ' more to reach ',
                              tierDetails.nextTierName
                            )
                          ),

                        !tierDetails.nextTierName &&
                          React.createElement(
                            'div',
                            {
                              className: 'mt-4 pt-4 border-t border-white/30',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1233 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-sm font-medium',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1234 },
                              },
                              '🏆 Maximum Tier Achieved!'
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs opacity-80 mt-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1235 },
                              },
                              "You've reached the highest tier. Enjoy exclusive Diamond benefits."
                            )
                          ),

                        React.createElement(
                          'div',
                          {
                            className: 'mt-4 pt-4 border-t border-white/30',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1239 },
                          },
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs opacity-80 flex items-center gap-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1240 },
                            },
                            React.createElement(
                              'span',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1241 },
                              },
                              tierDetails.config.icon,
                              ' ',
                              tierDetails.tierName,
                              ' Benefits:'
                            ),
                            tierDetails.config.benefits.slice(0, 2).join(' • ')
                          )
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: 'bg-stone-50 p-4 rounded-sm border border-stone-200',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1247 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center gap-3',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1248 },
                          },
                          React.createElement(
                            'div',
                            {
                              className:
                                'w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1249 },
                            },
                            React.createElement(Star, {
                              size: 16,
                              className: 'text-gold-600',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1250 },
                            })
                          ),
                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1252 },
                            },
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs font-bold text-stone-900',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1253 },
                              },
                              'How points work'
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-[10px] text-stone-500',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1254 },
                              },
                              'Earn 1 point for every ₱100 spent. Points never expire and can be redeemed for exclusive rewards.'
                            )
                          )
                        )
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className: 'perspective-1000 group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1260 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: `relative w-full aspect-[1.6/1] bg-gradient-to-br ${tierDetails.tierName === 'Diamond' ? 'from-stone-800 via-stone-900 to-black' : 'from-stone-800 to-stone-900'} rounded-xl shadow-2xl p-6 text-white overflow-hidden transition-transform duration-500 transform group-hover:rotate-y-6 group-hover:scale-105 border border-white/10`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1261 },
                        },
                        React.createElement('div', {
                          className:
                            'absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-20',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1262 },
                        }),
                        React.createElement('div', {
                          className:
                            "absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 z-0",
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1263 },
                        }),

                        React.createElement(
                          'div',
                          {
                            className: 'absolute top-4 right-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1265 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: `${tierDetails.config.badgeColor} text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg`,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1266 },
                            },
                            React.createElement(
                              'span',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1267 },
                              },
                              tierDetails.config.icon
                            ),
                            React.createElement(
                              'span',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1268 },
                              },
                              tierDetails.tierName
                            )
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'relative z-10 flex flex-col justify-between h-full',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1272 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex items-center gap-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1273 },
                            },
                            React.createElement(Gem, {
                              className: 'text-gold-400',
                              size: 24,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1274 },
                            }),
                            React.createElement(
                              'span',
                              {
                                className: 'font-serif text-lg tracking-wide',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1275 },
                              },
                              'Sparkling Gems'
                            )
                          ),

                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1278 },
                            },
                            React.createElement(
                              'div',
                              {
                                className: 'mb-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1279 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1280 },
                                },
                                'Member Since'
                              ),
                              React.createElement(
                                'span',
                                {
                                  className: 'text-sm font-light text-stone-300',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1281 },
                                },
                                memberSinceDate
                              )
                            ),

                            React.createElement(
                              'div',
                              {
                                className: 'mb-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1284 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1285 },
                                },
                                'Member Name'
                              ),
                              React.createElement(
                                'span',
                                {
                                  className: 'font-serif text-xl tracking-wide',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1286 },
                                },
                                profileData.firstName,
                                ' ',
                                profileData.lastName
                              )
                            ),

                            React.createElement(
                              'div',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1289 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'block text-[10px] uppercase tracking-widest text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1290 },
                                },
                                'Member ID'
                              ),
                              React.createElement(
                                'span',
                                {
                                  className: 'font-mono text-sm text-stone-300',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1291 },
                                },
                                memberId
                              )
                            )
                          ),

                          React.createElement(
                            'div',
                            {
                              className: 'absolute bottom-4 right-4 opacity-20',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1295 },
                            },
                            React.createElement(QrCode, {
                              size: 48,
                              className: 'text-white',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1296 },
                            })
                          )
                        )
                      )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'grid grid-cols-1 md:grid-cols-3 gap-6 mt-6',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1303 },
                    },
                    React.createElement(
                      'button',
                      {
                        onClick: () => setActiveTab('orders'),
                        className:
                          'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1304 },
                      },

                      React.createElement(
                        'div',
                        {
                          className:
                            'bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1308 },
                        },
                        React.createElement(Package, {
                          size: 22,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1309 },
                        })
                      ),
                      React.createElement(
                        'h3',
                        {
                          className: 'font-bold text-stone-900 mb-1 group-hover:text-gold-600',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1311 },
                        },
                        'Track Orders'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-500',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1312 },
                        },
                        'View status of recent purchases'
                      )
                    ),

                    React.createElement(
                      'button',
                      {
                        onClick: () => setActiveTab('payment-proof'),
                        className:
                          'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1315 },
                      },

                      React.createElement(
                        'div',
                        {
                          className:
                            'bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1319 },
                        },
                        React.createElement(Upload, {
                          size: 22,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1320 },
                        })
                      ),
                      React.createElement(
                        'h3',
                        {
                          className: 'font-bold text-stone-900 mb-1 group-hover:text-gold-600',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1322 },
                        },
                        'Upload Proof'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-500',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1323 },
                        },
                        'Submit receipt for pending orders'
                      )
                    ),

                    React.createElement(
                      'button',
                      {
                        onClick: () => onNavigate('contact'),
                        className:
                          'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md hover:border-gold-300 transition-all text-left group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1326 },
                      },

                      React.createElement(
                        'div',
                        {
                          className:
                            'bg-stone-100 w-12 h-12 rounded-full flex items-center justify-center text-stone-600 mb-4 group-hover:bg-gold-50 group-hover:text-gold-600 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1330 },
                        },
                        React.createElement(Calendar, {
                          size: 22,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1331 },
                        })
                      ),
                      React.createElement(
                        'h3',
                        {
                          className: 'font-bold text-stone-900 mb-1 group-hover:text-gold-600',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1333 },
                        },
                        'Book Appointment'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs text-stone-500',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1334 },
                        },
                        'Schedule a private viewing'
                      )
                    )
                  )
                )
        );

      case 'payment-proof':
        return React.createElement(
          'div',
          {
            className: 'space-y-6 animate-fade-in',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1344 },
          },
          React.createElement(
            'div',
            {
              className:
                'flex flex-col md:flex-row justify-between items-start md:items-center border-b border-stone-200 pb-4 gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1345 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1346 } },
              React.createElement(
                'h2',
                {
                  className: 'font-serif text-2xl text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1347 },
                },
                'Payment Verification'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-500 mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1348 },
                },
                'Upload screenshots of bank transfers for manual verification.'
              )
            ),
            pendingOrders.length > 0 &&
              React.createElement(
                'button',
                {
                  onClick: openPaymentModal,
                  className:
                    'bg-gold-600 text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-gold-500 transition-colors shadow-md flex items-center gap-2 rounded-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1351 },
                },

                React.createElement(Upload, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1355 },
                }),
                ' Submit Payment Proof'
              )
          ),

          pendingOrders.length > 0
            ? React.createElement(
                'div',
                {
                  className: 'grid grid-cols-1 gap-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1361 },
                },
                pendingOrders.map((order) => {
                  const orderAny = order;
                  const hasExistingProof = !!(
                    order.paymentProof ||
                    order.payment_proof ||
                    order.file_path
                  );

                  return React.createElement(
                    'div',
                    {
                      key: order.id,
                      className: `bg-white border rounded-sm shadow-sm ${order.status === 'Rejected' ? 'border-red-300' : 'border-stone-200'}`,
                    },
                    order.status === 'Rejected' &&
                      React.createElement(
                        'div',
                        {
                          className:
                            'px-6 py-3 bg-red-50 border-b border-red-200 text-sm text-red-700 flex items-center gap-2',
                        },
                        React.createElement(
                          'span',
                          { className: 'font-bold' },
                          'Payment Rejected:'
                        ),
                        React.createElement(
                          'span',
                          null,
                          order.rejection_reason ||
                            order.rejectionReason ||
                            'Please re-upload your payment proof.'
                        )
                      ),
                    React.createElement(
                      'div',
                      { className: 'p-6 flex flex-col md:flex-row gap-8' },
                      React.createElement(
                        'div',
                        {
                          className: 'flex-1 space-y-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1368 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex justify-between items-start',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1369 },
                          },
                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1370 },
                            },
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[10px] uppercase tracking-widest text-stone-500 font-bold block mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1371 },
                              },
                              'Order Ref'
                            ),
                            React.createElement(
                              'span',
                              {
                                className: 'font-mono text-lg text-stone-900',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1372 },
                              },
                              orderAny.order_number || order.id
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs text-stone-500',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1373 },
                              },
                              order.date || 'N/A'
                            )
                          ),
                          React.createElement(
                            'div',
                            {
                              className: 'text-right',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1375 },
                            },
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[10px] uppercase tracking-widest text-stone-500 font-bold block mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1376 },
                              },
                              'Amount Due'
                            ),
                            React.createElement(
                              'span',
                              {
                                className: 'font-serif text-xl text-stone-900',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1377 },
                              },
                              formatCurrency(order.total)
                            )
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'bg-stone-50 p-4 border border-stone-100 rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1381 },
                          },
                          React.createElement(
                            'p',
                            {
                              className:
                                'text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1382 },
                            },
                            'Bank Details'
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-sm font-bold text-stone-800',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1383 },
                            },
                            defaultBank
                              ? `${defaultBank.bank_name}: ${defaultBank.account_number}`
                              : 'Loading...'
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-stone-600',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1384 },
                            },
                            defaultBank?.account_name || ''
                          )
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: 'md:w-1/3 flex flex-col justify-center',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1388 },
                        },
                        React.createElement(
                          'label',
                          {
                            className:
                              'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1389 },
                          },
                          'Proof of Payment'
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col gap-3',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1391 },
                          },
                          hasExistingProof
                            ? React.createElement(
                                'div',
                                {
                                  className:
                                    'relative group w-full h-32 bg-stone-100 border border-stone-200 rounded-sm overflow-hidden cursor-pointer',
                                  onClick: () =>
                                    openLightbox(
                                      getAbsoluteUrl(
                                        order.paymentProof || order.payment_proof || order.file_path
                                      )
                                    ),
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1393 },
                                },

                                React.createElement('img', {
                                  src: getAbsoluteUrl(
                                    order.paymentProof || order.payment_proof || order.file_path
                                  ),
                                  alt: 'Proof',
                                  className: 'w-full h-full object-cover',
                                  onError: (e) => {
                                    e.target.src = PLACEHOLDER_IMAGE;
                                  },
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1397 },
                                }),
                                React.createElement(
                                  'div',
                                  {
                                    className:
                                      'absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1405 },
                                  },
                                  React.createElement(Eye, {
                                    size: 24,
                                    className: 'text-white',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1406 },
                                  })
                                )
                              )
                            : React.createElement(
                                'div',
                                {
                                  className:
                                    'w-full h-32 bg-stone-50 border-2 border-dashed border-stone-300 rounded-sm flex flex-col items-center justify-center text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1410 },
                                },
                                React.createElement(ImageIcon, {
                                  size: 24,
                                  className: 'mb-2',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1411 },
                                }),
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[10px]',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1412 },
                                  },
                                  'No image uploaded'
                                )
                              ),

                          React.createElement(
                            'label',
                            {
                              className:
                                'cursor-pointer bg-stone-900 text-white w-full py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 rounded-sm shadow-sm text-center',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1416 },
                            },
                            React.createElement(Upload, {
                              size: 14,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1417 },
                            }),
                            hasExistingProof ? 'Replace Image' : 'Upload Receipt',
                            React.createElement('input', {
                              type: 'file',
                              className: 'hidden',
                              accept: 'image/*',
                              onChange: (e) => handleProofUpload(e, order.id),
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1419 },
                            })
                          )
                        )
                      )
                    ) /* close inner p-6 flex div */
                  );
                })
              )
            : React.createElement(
                'div',
                {
                  className: 'text-center py-20 bg-stone-50 rounded-sm border border-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1433 },
                },
                React.createElement(CheckCircle2, {
                  className: 'mx-auto text-green-500 mb-6',
                  size: 48,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1434 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500 font-serif text-xl mb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1435 },
                  },
                  'All payments verified.'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-400 text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1436 },
                  },
                  'You have no pending orders requiring proof of payment.'
                )
              )
        );

      case 'orders':
        return React.createElement(
          'div',
          {
            className: 'space-y-6 animate-fade-in',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1444 },
          },
          React.createElement(
            'div',
            {
              className:
                'flex flex-col md:flex-row justify-between items-end border-b border-stone-200 pb-4 gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1445 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1446 } },
              React.createElement(
                'h2',
                {
                  className: 'font-serif text-2xl text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1447 },
                },
                'Order History'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-500 mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1448 },
                },
                'Track your orders and buy items again.'
              )
            ),

            React.createElement(
              'div',
              {
                className: 'flex items-center gap-3 w-full md:w-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1451 },
              },
              React.createElement(
                'div',
                {
                  className: 'relative flex-1 md:w-64',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1452 },
                },
                React.createElement(Search, {
                  className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1453 },
                }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Search Order ID or Item',
                  value: orderSearch,
                  onChange: (e) => setOrderSearch(e.target.value),
                  className:
                    'w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1454 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'relative',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1462 },
                },
                React.createElement(
                  'select',
                  {
                    value: orderFilter,
                    onChange: (e) => setOrderFilter(e.target.value),
                    className:
                      'appearance-none pl-4 pr-8 py-2 text-sm border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none bg-white cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1463 },
                  },

                  React.createElement(
                    'option',
                    {
                      value: 'All',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1468 },
                    },
                    'All Status'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Pending',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1469 },
                    },
                    'Pending'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Paid',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1470 },
                    },
                    'Paid'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Processing',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1471 },
                    },
                    'Preparing'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Shipped',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1472 },
                    },
                    'Shipped'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Ready for Pickup',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1473 },
                    },
                    'Ready for Pickup'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Picked Up',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1474 },
                    },
                    'Picked Up'
                  ),
                  React.createElement(
                    'option',
                    {
                      value: 'Delivered',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1475 },
                    },
                    'Delivered'
                  )
                ),
                React.createElement(Filter, {
                  className:
                    'absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none',
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1477 },
                })
              )
            )
          ),

          filteredOrders.length > 0
            ? React.createElement(
                'div',
                {
                  className: 'space-y-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1483 },
                },
                filteredOrders.map((order) => {
                  const orderAny = order;
                  const isPickup =
                    orderAny.deliveryType === 'pickup' ||
                    orderAny.delivery_type === 'pickup' ||
                    (orderAny.shippingAddress && orderAny.shippingAddress.includes('Store Pickup'));
                  const isComplete = isOrderComplete(order.status);

                  return React.createElement(
                    'div',
                    {
                      key: order.id,
                      className: `bg-white border rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 ${isComplete ? 'border-green-200' : 'border-stone-200'}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1492 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: `p-4 md:p-6 border-b flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${isComplete ? 'bg-green-50' : 'bg-stone-50'}`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1493 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex flex-col md:flex-row md:items-center gap-4 md:gap-8',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1494 },
                        },
                        React.createElement(
                          'div',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1495 } },
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[10px] uppercase tracking-widest text-stone-500 block mb-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1496 },
                            },
                            'Order Placed'
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'text-sm font-bold text-stone-800',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1497 },
                            },
                            order.orderDate
                              ? new Date(order.orderDate).toLocaleString('en-PH', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true,
                                })
                              : order.date || 'N/A'
                          )
                        ),
                        React.createElement(
                          'div',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1508 } },
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[10px] uppercase tracking-widest text-stone-500 block mb-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1509 },
                            },
                            'Total Amount'
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'text-sm font-serif text-stone-800',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1510 },
                            },
                            formatCurrency(order.totalAmount || order.total)
                          )
                        ),
                        React.createElement(
                          'div',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1512 } },
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[10px] uppercase tracking-widest text-stone-500 block mb-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1513 },
                            },
                            'Order No.'
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'text-sm font-mono text-stone-600',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1514 },
                            },
                            orderAny.order_number || order.id
                          )
                        ),
                        isPickup &&
                          React.createElement(
                            'div',
                            {
                              className:
                                'flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1517 },
                            },
                            React.createElement(Store, {
                              size: 12,
                              className: 'text-amber-600',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1518 },
                            }),
                            React.createElement(
                              'span',
                              {
                                className: 'text-[9px] text-amber-700 font-medium',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1519 },
                              },
                              'Pickup'
                            )
                          ),
                        !isPickup &&
                          React.createElement(
                            'div',
                            {
                              className:
                                'flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1523 },
                            },
                            React.createElement(Truck, {
                              size: 12,
                              className: 'text-blue-600',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1524 },
                            }),
                            React.createElement(
                              'span',
                              {
                                className: 'text-[9px] text-blue-700 font-medium',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1525 },
                              },
                              'Shipping'
                            )
                          )
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center gap-3',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1529 },
                        },
                        React.createElement(
                          'button',
                          {
                            onClick: () => toggleOrderExpand(order.id),
                            className:
                              'bg-white border border-stone-200 px-4 py-2 text-xs uppercase tracking-wider font-bold text-stone-600 hover:text-gold-600 hover:border-gold-400 transition-colors rounded-sm flex items-center gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1530 },
                          },

                          expandedOrder === order.id ? 'Hide Details' : 'View Details',
                          expandedOrder === order.id
                            ? React.createElement(ChevronUp, {
                                size: 14,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1535 },
                              })
                            : React.createElement(ChevronDown, {
                                size: 14,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1535 },
                              })
                        ),
                        React.createElement(
                          'button',
                          {
                            onClick: () => downloadInvoice(order),
                            className:
                              'bg-white border border-stone-200 p-2 text-stone-500 hover:text-stone-900 rounded-sm flex items-center gap-1',
                            title: 'Download Invoice',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1537 },
                          },

                          React.createElement(Download, {
                            size: 14,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1542 },
                          })
                        )
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className: 'p-6',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1547 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex flex-col md:flex-row gap-6',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1548 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'md:w-1/4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1549 },
                          },
                          React.createElement(
                            'h4',
                            {
                              className:
                                'text-xs font-bold text-stone-900 uppercase tracking-wider mb-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1550 },
                            },
                            'Status'
                          ),
                          React.createElement(
                            'span',
                            {
                              className: `inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${getStatusStyle(order.status)}`,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1551 },
                            },
                            getStatusIcon(order.status),
                            isComplete ? 'Order Complete' : getDisplayStatus(order.status)
                          ),

                          !isPickup &&
                            React.createElement(
                              'div',
                              {
                                className: 'mt-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1557 },
                              },
                              React.createElement(
                                'div',
                                {
                                  className:
                                    'flex items-center gap-1 text-[9px] text-blue-600 bg-blue-50 px-2 py-1 rounded-sm border border-blue-100 w-fit',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1558 },
                                },
                                React.createElement(Truck, {
                                  size: 10,
                                  className: 'text-blue-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1559 },
                                }),
                                React.createElement(
                                  'span',
                                  {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1560 },
                                  },
                                  'Shipping'
                                )
                              )
                            ),

                          isPickup &&
                            React.createElement(
                              'div',
                              {
                                className:
                                  'mt-3 p-2 bg-amber-50 rounded-sm border border-amber-100',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1566 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className:
                                    'text-[10px] font-bold text-amber-700 flex items-center gap-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1567 },
                                },
                                React.createElement(Store, {
                                  size: 12,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1568 },
                                }),
                                ' Pickup Order'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] text-amber-600 mt-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1570 },
                                },
                                order.status === 'Ready for Pickup'
                                  ? '✅ Ready for pickup at our Jewelry Shop'
                                  : order.status === 'Picked Up'
                                    ? '📦 Order picked up'
                                    : order.status === 'Paid'
                                      ? '⏳ Payment verified, awaiting processing'
                                      : '⏳ Awaiting payment verification'
                              ),
                              order.status === 'Ready for Pickup' &&
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[9px] text-amber-600 mt-1 font-medium',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1580 },
                                  },
                                  'Please visit our Jewelry Shop to collect your order.'
                                )
                            ),

                          React.createElement(
                            'div',
                            {
                              className: 'mt-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1587 },
                            },
                            order.trackingNumber && !isPickup
                              ? React.createElement(
                                  React.Fragment,
                                  null,
                                  React.createElement(
                                    'span',
                                    {
                                      className: 'text-[10px] text-stone-500 uppercase block mb-1',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1590 },
                                    },
                                    'Tracking Number'
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      className: 'text-xs font-mono text-stone-700',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1591 },
                                    },
                                    order.trackingNumber
                                  )
                                )
                              : !isPickup && !order.trackingNumber
                                ? React.createElement(
                                    'div',
                                    {
                                      className: 'mt-2',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1594 },
                                    },
                                    React.createElement(
                                      'p',
                                      {
                                        className:
                                          'text-[10px] text-stone-400 italic flex items-center gap-1',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 1595 },
                                      },
                                      React.createElement(Truck, {
                                        size: 10,
                                        className: 'text-stone-400',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 1596 },
                                      }),
                                      'Tracking number will be assigned soon'
                                    )
                                  )
                                : null
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'flex-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1604 },
                          },
                          React.createElement(
                            'h4',
                            {
                              className:
                                'text-xs font-bold text-stone-900 uppercase tracking-wider mb-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1605 },
                            },
                            'Items'
                          ),
                          React.createElement(
                            'div',
                            {
                              className: 'space-y-4',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1606 },
                            },
                            order.items &&
                              order.items.map((item, idx) => {
                                const itemKey = `${order.id}-${idx}`;
                                const imageUrl = getProductImage(item, item.id);

                                return React.createElement(
                                  'div',
                                  {
                                    key: itemKey,
                                    className: 'flex gap-4 items-center group',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1612 },
                                  },
                                  React.createElement(
                                    'div',
                                    {
                                      className:
                                        'w-16 h-16 bg-stone-100 rounded-sm overflow-hidden border border-stone-200 flex-shrink-0 cursor-zoom-in',
                                      onClick: () => openLightbox(imageUrl),
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1613 },
                                    },

                                    React.createElement('img', {
                                      src: imageUrl,
                                      alt: item.name || 'Product',
                                      className:
                                        'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500',
                                      onError: (e) => {
                                        e.target.src = PLACEHOLDER_IMAGE;
                                      },
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1617 },
                                    })
                                  ),

                                  React.createElement(
                                    'div',
                                    {
                                      className: 'flex-1',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1627 },
                                    },
                                    React.createElement(
                                      'p',
                                      {
                                        className: 'text-sm font-serif text-stone-900',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 1628 },
                                      },
                                      item.name || item.product_name || 'Product'
                                    ),
                                    React.createElement(
                                      'p',
                                      {
                                        className: 'text-xs text-stone-500',
                                        __self: this,
                                        __source: { fileName: _jsxFileName, lineNumber: 1629 },
                                      },
                                      'Qty: ',
                                      item.quantity || 1,
                                      ' • ',
                                      formatCurrency(item.price || item.product_price)
                                    )
                                  )
                                );
                              })
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className:
                              'md:w-1/5 flex flex-col gap-2 justify-center border-t md:border-t-0 md:border-l border-stone-100 pt-4 md:pt-0 md:pl-6',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1637 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () => setTrackingModalOrder(order),
                              className:
                                'w-full bg-stone-900 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-gold-600 transition-colors shadow-sm rounded-sm flex items-center justify-center gap-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1638 },
                            },

                            React.createElement(Truck, {
                              size: 14,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1642 },
                            }),
                            'Track Order'
                          ),

                          isPickup &&
                            order.status === 'Ready for Pickup' &&
                            React.createElement(
                              'div',
                              {
                                className:
                                  'text-center p-2 bg-amber-50 border border-amber-200 rounded-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1647 },
                              },
                              React.createElement(Store, {
                                size: 16,
                                className: 'mx-auto text-amber-600 mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1648 },
                              }),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] text-amber-700',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1649 },
                                },
                                'Ready for pickup'
                              )
                            ),

                          isPickup &&
                            order.status !== 'Ready for Pickup' &&
                            order.status !== 'Picked Up' &&
                            React.createElement(
                              'div',
                              {
                                className:
                                  'text-center p-2 bg-amber-50/50 border border-amber-100 rounded-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1654 },
                              },
                              React.createElement(Store, {
                                size: 16,
                                className: 'mx-auto text-amber-500 mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1655 },
                              }),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[9px] text-amber-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1656 },
                                },
                                'Pickup order'
                              )
                            )
                        )
                      ),

                      expandedOrder === order.id &&
                        React.createElement(
                          'div',
                          {
                            className:
                              'mt-6 pt-6 border-t border-stone-100 grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1663 },
                          },
                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1664 },
                            },
                            React.createElement(
                              'h5',
                              {
                                className: 'text-xs font-bold text-stone-900 mb-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1665 },
                              },
                              isPickup ? 'Pickup Information' : 'Shipping Address'
                            ),
                            isPickup
                              ? React.createElement(
                                  'div',
                                  {
                                    className: 'text-sm text-stone-600 leading-relaxed',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1669 },
                                  },
                                  React.createElement(
                                    'p',
                                    {
                                      className: 'font-medium',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1670 },
                                    },
                                    'Pickup Person:'
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1671 },
                                    },
                                    orderAny.pickup_name ||
                                      order.customerName ||
                                      `${profileData.firstName} ${profileData.lastName}`
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      className: 'mt-2 font-medium',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1672 },
                                    },
                                    'Contact:'
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1673 },
                                    },
                                    orderAny.pickup_phone ||
                                      orderAny.customer_phone ||
                                      profileData.phone
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      className: 'mt-2 font-medium',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1674 },
                                    },
                                    'Location:'
                                  ),
                                  React.createElement(
                                    'p',
                                    {
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1675 },
                                    },
                                    'Sparkling Gems Jewelry Shop',
                                    React.createElement('br', {
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1675 },
                                    }),
                                    '2F, San Antonio Building, Corumi, Quezon City'
                                  )
                                )
                              : React.createElement(
                                  'p',
                                  {
                                    className: 'text-sm text-stone-600 leading-relaxed',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1678 },
                                  },
                                  order.customerName ||
                                    orderAny.customer_name ||
                                    `${profileData.firstName} ${profileData.lastName}`,
                                  React.createElement('br', {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1679 },
                                  }),
                                  order.shippingAddress ||
                                    orderAny.shipping_address ||
                                    profileData.address,
                                  React.createElement('br', {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1680 },
                                  }),
                                  profileData.city,
                                  ', ',
                                  profileData.postalCode,
                                  React.createElement('br', {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1681 },
                                  }),
                                  orderAny.customer_phone || profileData.phone
                                )
                          ),
                          React.createElement(
                            'div',
                            {
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1686 },
                            },
                            React.createElement(
                              'h5',
                              {
                                className: 'text-xs font-bold text-stone-900 mb-2',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1687 },
                              },
                              'Payment Information'
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'flex items-center gap-2 text-sm text-stone-600 mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1688 },
                              },
                              order.paymentMethod === 'Bank Transfer' ||
                                orderAny.payment_method === 'Bank Transfer'
                                ? React.createElement(
                                    'span',
                                    {
                                      className: 'text-gold-600 font-bold text-lg',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1690 },
                                    },
                                    '₱'
                                  )
                                : React.createElement(CreditCard, {
                                    size: 16,
                                    className: 'text-stone-400',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1691 },
                                  }),

                              order.paymentMethod || orderAny.payment_method || 'Bank Transfer'
                            ),
                            React.createElement(
                              'p',
                              {
                                className: 'text-xs text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1695 },
                              },
                              'Total: ₱',
                              (order.totalAmount || order.total || 0).toLocaleString()
                            ),
                            order.transactionId &&
                              React.createElement(
                                'p',
                                {
                                  className: 'text-xs text-stone-400 mt-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1697 },
                                },
                                'Transaction: ',
                                order.transactionId
                              ),
                            orderAny.transaction_id &&
                              !order.transactionId &&
                              React.createElement(
                                'p',
                                {
                                  className: 'text-xs text-stone-400 mt-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1700 },
                                },
                                'Transaction: ',
                                orderAny.transaction_id
                              )
                          )
                        )
                    )
                  );
                })
              )
            : React.createElement(
                'div',
                {
                  className: 'text-center py-20 bg-stone-50 rounded-sm border border-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1711 },
                },
                React.createElement(ShoppingBag, {
                  className: 'mx-auto text-stone-300 mb-6',
                  size: 64,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1712 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500 font-serif text-xl mb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1713 },
                  },
                  'No orders found.'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-400 text-sm mb-8',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1714 },
                  },
                  "You haven't placed any orders yet, or no orders match your search."
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => onNavigate('shop'),
                    className:
                      'px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1715 },
                  },
                  'Start Shopping'
                )
              )
        );

      case 'wishlist':
        return React.createElement(
          'div',
          {
            className: 'space-y-6 animate-fade-in',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1725 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-center border-b border-stone-200 pb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1726 },
            },
            React.createElement(
              'h2',
              {
                className: 'font-serif text-2xl text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1727 },
              },
              'My Collection Wishlist'
            ),
            React.createElement(
              'div',
              {
                className: 'text-sm text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1728 },
              },
              wishlist.length,
              ' Items'
            )
          ),

          wishlist.length > 0
            ? React.createElement(
                'div',
                {
                  className:
                    'bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1732 },
                },
                React.createElement(
                  'div',
                  {
                    className:
                      'p-4 border-b border-stone-100 bg-stone-50/50 flex justify-between items-center',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1733 },
                  },
                  React.createElement(
                    'button',
                    {
                      onClick: () =>
                        onToggleAllWishlistSelection &&
                        onToggleAllWishlistSelection(!isAllSelected),
                      className:
                        'flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-stone-600 hover:text-gold-600 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1734 },
                    },

                    isAllSelected
                      ? React.createElement(CheckSquare, {
                          size: 16,
                          className: 'text-gold-600',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1738 },
                        })
                      : React.createElement(Square, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1738 },
                        }),
                    'Select All'
                  ),

                  selectedWishlistItems.length > 0 &&
                    React.createElement(
                      'div',
                      {
                        className: 'flex items-center gap-3',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1743 },
                      },
                      React.createElement(
                        'button',
                        {
                          onClick: handleBulkDelete,
                          className:
                            'flex items-center gap-1 text-xs uppercase tracking-wider text-red-500 hover:text-red-700 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1744 },
                        },

                        React.createElement(Trash2, {
                          size: 14,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1748 },
                        }),
                        ' Delete Selected'
                      )
                    )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'divide-y divide-stone-100',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1761 },
                  },
                  wishlist.map((item) => {
                    const productImage = getProductImage(item, item.id);

                    return React.createElement(
                      'div',
                      {
                        key: item.id,
                        className:
                          'p-6 flex flex-col md:flex-row items-center gap-6 hover:bg-stone-50/30 transition-colors group',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1766 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center gap-4 w-full md:w-auto',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1767 },
                        },
                        React.createElement(
                          'button',
                          {
                            onClick: () =>
                              onToggleWishlistSelection && onToggleWishlistSelection(item.id),
                            className: 'text-stone-400 hover:text-gold-600 transition-colors',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1768 },
                          },

                          item.selected
                            ? React.createElement(CheckSquare, {
                                size: 20,
                                className: 'text-gold-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1772 },
                              })
                            : React.createElement(Square, {
                                size: 20,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1772 },
                              })
                        ),
                        React.createElement(
                          'div',
                          {
                            onClick: () => onSelectProduct && onSelectProduct(item),
                            className:
                              'w-24 h-24 bg-stone-100 border border-stone-200 overflow-hidden cursor-pointer relative rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1774 },
                          },

                          React.createElement('img', {
                            src: wishlistImageErrors[item.id] ? PLACEHOLDER_IMAGE : productImage,
                            alt: item.name,
                            className:
                              'w-full h-full object-cover transition-transform duration-500 group-hover:scale-105',
                            onError: () => handleWishlistImageError(item.id),
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1778 },
                          })
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: 'flex-1 text-center md:text-left',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1787 },
                        },
                        React.createElement(
                          'h3',
                          {
                            onClick: () => onSelectProduct && onSelectProduct(item),
                            className:
                              'font-serif text-lg text-stone-900 cursor-pointer hover:text-gold-600 transition-colors',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1788 },
                          },

                          item.name
                        ),
                        React.createElement(
                          'p',
                          {
                            className: 'text-xs text-stone-500 mb-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1794 },
                          },
                          item.description
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'text-sm font-bold text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1795 },
                          },
                          formatCurrency(item.price)
                        )
                      ),

                      React.createElement(
                        'div',
                        {
                          className: 'flex flex-col md:flex-row items-center gap-6',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1798 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center border border-stone-200 rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1799 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () =>
                                onUpdateWishlistQuantity && onUpdateWishlistQuantity(item.id, -1),
                              className:
                                'p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1800 },
                            },

                            React.createElement(Minus, {
                              size: 14,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1804 },
                            })
                          ),
                          React.createElement(
                            'span',
                            {
                              className: 'w-10 text-center text-sm font-mono text-stone-900',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1806 },
                            },
                            item.quantity || 1
                          ),
                          React.createElement(
                            'button',
                            {
                              onClick: () =>
                                onUpdateWishlistQuantity && onUpdateWishlistQuantity(item.id, 1),
                              className:
                                'p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1807 },
                            },

                            React.createElement(Plus, {
                              size: 14,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1811 },
                            })
                          )
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1815 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () =>
                                onRemoveFromWishlist && onRemoveFromWishlist([item.id]),
                              className:
                                'p-2 border border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-200 rounded-sm transition-colors',
                              title: 'Remove',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1822 },
                            },

                            React.createElement(Trash2, {
                              size: 16,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1827 },
                            })
                          )
                        )
                      )
                    );
                  })
                )
              )
            : React.createElement(
                'div',
                {
                  className: 'text-center py-12 bg-stone-50 rounded-sm border border-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1837 },
                },
                React.createElement(Heart, {
                  className: 'mx-auto text-stone-300 mb-4',
                  size: 48,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1838 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500 font-serif text-lg',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1839 },
                  },
                  'Your wishlist is empty.'
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => onNavigate('shop'),
                    className:
                      'mt-4 text-xs uppercase font-bold text-gold-600 hover:text-gold-500 underline',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1840 },
                  },
                  'Browse Collections'
                )
              )
        );

      case 'settings':
        return React.createElement(
          'div',
          {
            className: 'space-y-8 animate-fade-in',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1848 },
          },
          isLoadingProfile
            ? React.createElement(
                'div',
                {
                  className: 'flex justify-center py-12',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1850 },
                },
                React.createElement(Loader, {
                  size: 32,
                  className: 'animate-spin text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1851 },
                })
              )
            : React.createElement(
                React.Fragment,
                null,
                React.createElement(
                  'div',
                  {
                    className: 'flex justify-between items-center border-b border-stone-200 pb-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1855 },
                  },
                  React.createElement(
                    'h2',
                    {
                      className: 'font-serif text-2xl text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1856 },
                    },
                    'Account Settings'
                  ),
                  React.createElement(
                    'button',
                    {
                      onClick: () => {
                        if (isEditing) {
                          const form = document.getElementById('settings-form');
                          if (form) {
                            const event = new Event('submit', { cancelable: true, bubbles: true });
                            form.dispatchEvent(event);
                          }
                        } else {
                          setIsEditing(true);
                        }
                      },
                      className:
                        'flex items-center gap-2 text-xs uppercase tracking-widest text-gold-600 hover:text-gold-500 font-bold bg-gold-50 px-4 py-2 rounded-sm border border-gold-100 hover:border-gold-300 transition-all',
                      disabled: isUploadingPhoto,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1857 },
                    },

                    isEditing
                      ? React.createElement(
                          React.Fragment,
                          null,
                          isUploadingPhoto
                            ? React.createElement(
                                React.Fragment,
                                null,
                                React.createElement(Loader, {
                                  size: 14,
                                  className: 'animate-spin',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1875 },
                                }),
                                ' Saving...'
                              )
                            : React.createElement(
                                React.Fragment,
                                null,
                                React.createElement(Save, {
                                  size: 14,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1877 },
                                }),
                                ' Save Changes'
                              )
                        )
                      : React.createElement(
                          React.Fragment,
                          null,
                          React.createElement(Edit3, {
                            size: 14,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1881 },
                          }),
                          ' Edit Profile'
                        )
                  )
                ),

                React.createElement(
                  'form',
                  {
                    id: 'settings-form',
                    onSubmit: handleSave,
                    className: 'grid grid-cols-1 md:grid-cols-2 gap-8',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1886 },
                  },
                  React.createElement(
                    'div',
                    {
                      className:
                        'md:col-span-2 bg-white p-8 border border-stone-200 shadow-sm rounded-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1887 },
                    },
                    React.createElement(
                      'h3',
                      {
                        className:
                          'text-xs uppercase tracking-widest text-stone-400 font-bold mb-6 flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1888 },
                      },
                      React.createElement(User, {
                        size: 14,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1889 },
                      }),
                      ' Personal Information'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'grid grid-cols-1 md:grid-cols-2 gap-8',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1891 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1892 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1893 },
                          },
                          'First Name'
                        ),
                        React.createElement('input', {
                          type: 'text',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.firstName,
                          onChange: (e) =>
                            setProfileData({ ...profileData, firstName: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          required: true,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1894 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1903 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1904 },
                          },
                          'Last Name'
                        ),
                        React.createElement('input', {
                          type: 'text',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.lastName,
                          onChange: (e) =>
                            setProfileData({ ...profileData, lastName: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          required: true,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1905 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1914 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1915 },
                          },
                          'Email'
                        ),
                        React.createElement('input', {
                          type: 'email',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.email,
                          onChange: (e) =>
                            setProfileData({ ...profileData, email: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          required: true,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1916 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1925 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1926 },
                          },
                          'Phone'
                        ),
                        React.createElement('input', {
                          type: 'text',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.phone,
                          onChange: (e) =>
                            setProfileData({ ...profileData, phone: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1927 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1935 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1936 },
                          },
                          'Address'
                        ),
                        React.createElement('input', {
                          type: 'text',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.address,
                          onChange: (e) =>
                            setProfileData({ ...profileData, address: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1937 },
                        })
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'space-y-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1945 },
                        },
                        React.createElement(
                          'label',
                          {
                            className: 'text-xs font-bold text-stone-500 uppercase',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1946 },
                          },
                          'City'
                        ),
                        React.createElement('input', {
                          type: 'text',
                          disabled: !isEditing || isUploadingPhoto,
                          value: profileData.city,
                          onChange: (e) => setProfileData({ ...profileData, city: e.target.value }),
                          className:
                            'w-full border-b border-stone-300 py-2 text-stone-900 focus:border-gold-600 focus:outline-none bg-transparent disabled:text-stone-600 disabled:border-stone-100 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1947 },
                        })
                      )
                    ),

                    saveSuccess &&
                      React.createElement(
                        'div',
                        {
                          className:
                            'mt-4 p-2 bg-green-50 border border-green-200 text-green-700 text-xs rounded-sm',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1958 },
                        },
                        'Profile updated successfully!'
                      )
                  )
                )
              )
        );
    }
  };

  return React.createElement(
    'div',
    {
      className: 'min-h-screen bg-stone-50 pt-20',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 1972 },
    },
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '280px' },
        },
        React.createElement('span', null, toast.message),
        React.createElement(
          'button',
          {
            onClick: () => setToast(null),
            className: 'ml-auto text-white/70 hover:text-white text-lg leading-none',
          },
          '×'
        )
      ),

    /* Tier Info Modal */
    showTierInfoModal &&
      React.createElement(TierInfoModal, {
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 1975 },
      }),

    /* Lightbox Modal */
    lightboxOpen &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[400] bg-black/95 flex items-center justify-center',
          onClick: closeLightbox,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1979 },
        },
        React.createElement(
          'button',
          {
            onClick: closeLightbox,
            className:
              'absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1980 },
          },
          React.createElement(XCircle, {
            size: 30,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1981 },
          })
        ),
        React.createElement('img', {
          src: lightboxImage,
          alt: 'Full size view',
          className: 'max-w-[90vw] max-h-[90vh] object-contain',
          onClick: (e) => e.stopPropagation(),
          onError: (e) => {
            e.target.src = PLACEHOLDER_IMAGE;
          },
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1983 },
        })
      ),

    showPhotoUploadModal &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1996 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
          onClick: () => setShowPhotoUploadModal(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1997 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1998 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6 flex justify-between items-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1999 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2000 } },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2001 },
                },
                'Update Profile Photo'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2002 },
                },
                'Choose a new profile picture (Max 20MB)'
              )
            ),
            React.createElement(
              'button',
              {
                onClick: () => setShowPhotoUploadModal(false),
                className: 'text-stone-400 hover:text-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2004 },
              },
              React.createElement(X, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2005 },
              })
            )
          ),

          React.createElement(
            'div',
            {
              className: 'p-8 space-y-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2009 },
            },
            React.createElement('input', {
              type: 'file',
              ref: fileInputRef,
              onChange: handlePhotoSelect,
              accept: 'image/*',
              className: 'hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2010 },
            }),

            React.createElement(
              'div',
              {
                className: 'flex flex-col items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2018 },
              },
              React.createElement(
                'div',
                {
                  className:
                    'w-40 h-40 rounded-full border-4 border-gold-200 overflow-hidden shadow-xl mb-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2019 },
                },
                React.createElement('img', {
                  key: profileData.avatar,
                  src: selectedPhoto || profileData.avatar || PLACEHOLDER_IMAGE,
                  alt: 'Profile Preview',
                  className: 'w-full h-full object-cover',
                  onError: (e) => {
                    e.target.src = PLACEHOLDER_IMAGE;
                  },
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2020 },
                })
              ),

              selectedPhoto
                ? React.createElement(
                    'p',
                    {
                      className: 'text-xs text-green-600 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2032 },
                    },
                    'New photo selected'
                  )
                : React.createElement(
                    'p',
                    {
                      className: 'text-xs text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2034 },
                    },
                    'Current profile photo'
                  )
            ),

            React.createElement(
              'div',
              {
                className: 'flex flex-col gap-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2038 },
              },
              React.createElement(
                'button',
                {
                  onClick: triggerFileInput,
                  className:
                    'w-full border-2 border-dashed border-stone-300 py-4 px-4 rounded-sm hover:border-gold-500 hover:bg-stone-50 transition-colors flex items-center justify-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2039 },
                },

                React.createElement(Camera, {
                  size: 18,
                  className: 'text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2043 },
                }),
                React.createElement(
                  'span',
                  {
                    className: 'text-sm font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2044 },
                  },
                  'Choose New Photo'
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'flex gap-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2047 },
                },
                React.createElement(
                  'button',
                  {
                    onClick: () => setShowPhotoUploadModal(false),
                    className:
                      'flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2048 },
                  },
                  'Cancel'
                ),
                React.createElement(
                  'button',
                  {
                    onClick: handlePhotoUpload,
                    disabled: !selectedPhoto || isUploadingPhoto,
                    className:
                      'flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2054 },
                  },

                  isUploadingPhoto
                    ? React.createElement(
                        React.Fragment,
                        null,
                        React.createElement('div', {
                          className:
                            'w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 2061 },
                        }),
                        'Uploading...'
                      )
                    : 'Upload Photo'
                )
              ),
              React.createElement(
                'p',
                {
                  className: 'text-[10px] text-stone-400 text-center mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2069 },
                },
                'Supported formats: JPG, PNG, GIF, WEBP (Max 20MB)'
              )
            )
          )
        )
      ),

    showPaymentModal &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2077 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
          onClick: () => setShowPaymentModal(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2078 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 2079 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6 flex justify-between items-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2080 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2081 } },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2082 },
                },
                'Submit Payment Proof'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2083 },
                },
                'Verify your bank transfer'
              )
            ),
            React.createElement(
              'button',
              {
                onClick: () => setShowPaymentModal(false),
                className: 'text-stone-400 hover:text-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2085 },
              },
              React.createElement(X, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2085 },
              })
            )
          ),

          React.createElement(
            'form',
            {
              onSubmit: handleSubmitPayment,
              className: 'p-8 space-y-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2088 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2089 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2090 },
                },
                'Select Order'
              ),
              React.createElement(
                'select',
                {
                  value: paymentForm.orderId,
                  onChange: (e) => setPaymentForm({ ...paymentForm, orderId: e.target.value }),
                  required: true,
                  className:
                    'w-full border border-stone-300 p-3 rounded-sm text-sm focus:border-gold-500 outline-none bg-white text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2091 },
                },

                React.createElement(
                  'option',
                  {
                    value: '',
                    disabled: true,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2097 },
                  },
                  'Select a pending order...'
                ),
                pendingOrders.map((order) => {
                  const orderAny = order;
                  return React.createElement(
                    'option',
                    {
                      key: order.id,
                      value: order.id,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2101 },
                    },
                    orderAny.order_number || order.id,
                    ' — ',
                    order.items &&
                      _optionalChain([
                        order,
                        'access',
                        (_58) => _58.items,
                        'access',
                        (_59) => _59[0],
                        'optionalAccess',
                        (_60) => _60.name,
                      ]),
                    ' ',
                    order.items && order.items.length > 1 ? `+${order.items.length - 1} more` : '',
                    ' — ',
                    formatCurrency(order.total)
                  );
                })
              )
            ),

            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2109 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2110 },
                },
                'Transaction Reference No.'
              ),
              React.createElement('input', {
                type: 'text',
                required: true,
                value: paymentForm.transactionRef,
                onChange: (e) => setPaymentForm({ ...paymentForm, transactionRef: e.target.value }),
                placeholder: 'e.g. TR-123456789',
                className:
                  'w-full border border-stone-300 p-3 rounded-sm text-sm focus:border-gold-500 outline-none bg-white text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2111 },
              })
            ),

            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2121 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2122 },
                },
                'Upload Screenshot'
              ),
              React.createElement(
                'div',
                {
                  className: 'flex gap-4 items-start',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2123 },
                },
                React.createElement(
                  'label',
                  {
                    className:
                      'flex-1 cursor-pointer border-2 border-dashed border-stone-300 rounded-sm p-4 flex flex-col items-center justify-center hover:border-gold-500 hover:bg-stone-50 transition-colors h-32',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2124 },
                  },
                  React.createElement(Upload, {
                    size: 24,
                    className: 'text-stone-400 mb-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2125 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-xs text-stone-500 font-medium',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2126 },
                    },
                    'Click to upload image'
                  ),
                  React.createElement('input', {
                    type: 'file',
                    required: true,
                    accept: 'image/*',
                    className: 'hidden',
                    onChange: handleModalFileChange,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2127 },
                  })
                ),
                paymentForm.proofPreview &&
                  React.createElement(
                    'div',
                    {
                      className:
                        'w-32 h-32 border border-stone-200 rounded-sm overflow-hidden bg-stone-100 flex-shrink-0 relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2136 },
                    },
                    React.createElement('img', {
                      src: paymentForm.proofPreview,
                      alt: 'Preview',
                      className: 'w-full h-full object-cover',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2137 },
                    }),
                    React.createElement(
                      'button',
                      {
                        type: 'button',
                        onClick: () => setPaymentForm({ ...paymentForm, proofPreview: null }),
                        className:
                          'absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 2138 },
                      },

                      React.createElement(X, {
                        size: 12,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 2143 },
                      })
                    )
                  )
              )
            ),

            React.createElement(
              'button',
              {
                type: 'submit',
                disabled:
                  !paymentForm.orderId ||
                  !paymentForm.transactionRef ||
                  !paymentForm.proofPreview ||
                  paymentForm.isUploading,
                className:
                  'w-full bg-stone-900 text-white py-3 uppercase text-xs tracking-widest hover:bg-gold-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2150 },
              },

              paymentForm.isUploading
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(Loader, {
                      size: 16,
                      className: 'animate-spin',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2156 },
                    }),
                    ' Uploading...'
                  )
                : 'Submit Verification'
            )
          )
        )
      ),

    trackingModalOrder &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2167 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
          onClick: () => setTrackingModalOrder(null),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2168 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden z-[301]',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 2169 },
          },

          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6 relative',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2171 },
            },
            React.createElement(
              'button',
              {
                onClick: () => setTrackingModalOrder(null),
                className: 'absolute top-4 right-4 text-stone-400 hover:text-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2172 },
              },
              React.createElement(X, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2173 },
              })
            ),
            React.createElement(
              'h3',
              {
                className: 'font-serif text-2xl mb-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2175 },
              },
              'Track Order'
            ),
            React.createElement(
              'p',
              {
                className: 'text-stone-400 text-xs uppercase tracking-widest',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2176 },
              },
              'Order #',
              trackingModalOrder.id
            ),

            orderDetails &&
              orderDetails.status &&
              React.createElement(
                'div',
                {
                  className:
                    'mt-2 inline-block px-2 py-1 bg-gold-100 text-gold-800 rounded-sm text-[10px] font-bold',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2179 },
                },
                'Current Status: ',
                getStatusDisplayText(orderDetails.status)
              )
          ),

          React.createElement(
            'div',
            {
              className: 'p-8',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2185 },
            },
            /* Timeline Progress Bar - Auto lights up based on current status */
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center mb-8 relative px-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2187 },
              },
              React.createElement('div', {
                className: 'absolute top-1/2 left-0 right-0 h-px bg-stone-200 -z-10',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2188 },
              }),
              ['Pending', 'Preparing', 'Shipped', 'Delivered'].map((step, idx) => {
                const steps = ['Pending', 'Preparing', 'Shipped', 'Delivered'];

                let currentStatus =
                  _optionalChain([orderDetails, 'optionalAccess', (_61) => _61.status]) ||
                  trackingModalOrder.status ||
                  'Pending';
                if (currentStatus === 'Processing') currentStatus = 'Preparing';

                const currentIdx = steps.indexOf(currentStatus);
                const stepIdx = steps.indexOf(step);
                const isCompleted = stepIdx <= currentIdx;
                const isCurrent = stepIdx === currentIdx;

                // Status-specific colors for the circles
                let circleBg = '';
                if (isCompleted) {
                  if (step === 'Pending') circleBg = 'bg-orange-600';
                  else if (step === 'Preparing') circleBg = 'bg-purple-600';
                  else if (step === 'Shipped') circleBg = 'bg-blue-600';
                  else if (step === 'Delivered') circleBg = 'bg-green-600';
                  else circleBg = 'bg-gold-600';
                } else {
                  circleBg = 'bg-stone-300';
                }

                return React.createElement(
                  'div',
                  {
                    key: step,
                    className: 'flex flex-col items-center gap-2 bg-white px-2 z-10 flex-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2213 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: `w-8 h-8 rounded-full ${circleBg} ring-2 ${isCompleted ? 'ring-gold-300' : 'ring-stone-200'} border-2 border-white shadow-md flex items-center justify-center`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2214 },
                    },
                    isCompleted &&
                      stepIdx < currentIdx &&
                      React.createElement(CheckCircle2, {
                        size: 14,
                        className: 'text-white',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 2216 },
                      }),
                    isCurrent &&
                      React.createElement('div', {
                        className: 'w-3 h-3 bg-white rounded-full animate-pulse',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 2219 },
                      })
                  ),
                  React.createElement(
                    'span',
                    {
                      className: `text-[10px] uppercase font-bold ${
                        isCurrent
                          ? 'text-gold-600'
                          : isCompleted
                            ? 'text-stone-800'
                            : 'text-stone-300'
                      }`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 2222 },
                    },
                    step
                  )
                );
              })
            ),

            React.createElement(
              'div',
              {
                className:
                  'bg-stone-50 p-4 border border-stone-200 rounded-sm mb-8 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2233 },
              },
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2234 } },
                React.createElement(
                  'span',
                  {
                    className: 'text-[10px] text-stone-500 uppercase font-bold block mb-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2235 },
                  },
                  'Tracking Number'
                ),
                React.createElement(
                  'span',
                  {
                    className: 'font-mono text-stone-900 text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 2236 },
                  },
                  _optionalChain([orderDetails, 'optionalAccess', (_62) => _62.trackingNumber]) ||
                    trackingModalOrder.trackingNumber ||
                    trackingModalOrder.tracking_number ||
                    'Pending Assignment'
                )
              ),
              React.createElement(Truck, {
                className: 'text-stone-300',
                size: 24,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2240 },
              })
            ),

            React.createElement(
              'div',
              {
                className:
                  'space-y-6 relative border-l border-stone-200 ml-3 pl-8 max-h-96 overflow-y-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2243 },
              },
              isLoadingTimeline
                ? React.createElement(
                    'div',
                    { className: 'flex justify-center py-8' },
                    React.createElement('div', {
                      className:
                        'w-8 h-8 border-4 border-gold-600 border-t-transparent rounded-full animate-spin',
                    })
                  )
                : timelineError
                  ? React.createElement(
                      'div',
                      { className: 'py-6 text-center' },
                      React.createElement(
                        'p',
                        { className: 'text-red-500 text-sm mb-3' },
                        timelineError
                      ),
                      React.createElement(
                        'button',
                        {
                          onClick: () => fetchTimeline(trackingModalOrder.id),
                          className: 'text-xs text-gold-600 underline hover:text-gold-500',
                        },
                        'Try again'
                      )
                    )
                  : orderTimeline && orderTimeline.length > 0
                    ? orderTimeline.map((event, idx) => {
                        const imageUrl = getTimelineImageUrl(event);
                        const hasImage = imageUrl && imageUrl !== PLACEHOLDER_IMAGE;

                        // Determine status color for the timeline event
                        const eventStatus = event.status || 'Update';
                        const isRejected = eventStatus === 'Rejected';
                        const isPaid = eventStatus === 'Paid';
                        const isDelivered =
                          eventStatus === 'Delivered' || eventStatus === 'Picked Up';
                        const isShipped = eventStatus === 'Shipped';
                        const isPreparing =
                          eventStatus === 'Preparing' || eventStatus === 'Processing';
                        const isPending = eventStatus === 'Pending';

                        let circleColor = 'bg-stone-300';
                        let ringColor = 'ring-stone-200';
                        let statusBadgeClass = '';
                        let statusTextColor = '';

                        if (isRejected) {
                          circleColor = 'bg-red-600';
                          ringColor = 'ring-red-300';
                          statusBadgeClass = 'bg-red-100 text-red-700 border-red-200';
                          statusTextColor = 'text-red-700';
                        } else if (isPaid) {
                          circleColor = 'bg-emerald-600';
                          ringColor = 'ring-emerald-300';
                          statusBadgeClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
                          statusTextColor = 'text-emerald-700';
                        } else if (isDelivered) {
                          circleColor = 'bg-green-600';
                          ringColor = 'ring-green-300';
                          statusBadgeClass = 'bg-green-100 text-green-700 border-green-200';
                          statusTextColor = 'text-green-700';
                        } else if (isShipped) {
                          circleColor = 'bg-blue-600';
                          ringColor = 'ring-blue-300';
                          statusBadgeClass = 'bg-blue-100 text-blue-700 border-blue-200';
                          statusTextColor = 'text-blue-700';
                        } else if (isPreparing) {
                          circleColor = 'bg-purple-600';
                          ringColor = 'ring-purple-300';
                          statusBadgeClass = 'bg-purple-100 text-purple-700 border-purple-200';
                          statusTextColor = 'text-purple-700';
                        } else if (isPending) {
                          circleColor = 'bg-orange-600';
                          ringColor = 'ring-orange-300';
                          statusBadgeClass = 'bg-orange-100 text-orange-700 border-orange-200';
                          statusTextColor = 'text-orange-700';
                        }

                        return React.createElement(
                          'div',
                          {
                            key: idx,
                            className: 'relative pb-6',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 2300 },
                          },
                          /* Timeline connector line between items */
                          idx !== orderTimeline.length - 1 &&
                            React.createElement('div', {
                              className: 'absolute left-[-9px] top-6 bottom-0 w-0.5 bg-stone-200',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 2303 },
                            }),

                          /* Circle marker - Full circle with proper styling */
                          React.createElement(
                            'div',
                            {
                              className: 'absolute -left-[37px] top-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 2307 },
                            },
                            React.createElement('div', {
                              className: `w-4 h-4 rounded-full ${circleColor} ring-2 ${ringColor} border-2 border-white shadow-sm`,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 2308 },
                            })
                          ),

                          /* Content */
                          React.createElement(
                            'div',
                            {
                              className: 'ml-0',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 2312 },
                            },
                            /* Status Badge */
                            React.createElement(
                              'div',
                              {
                                className: 'flex items-center gap-2 mb-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 2314 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className: `inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${statusBadgeClass}`,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 2315 },
                                },
                                eventStatus === 'Processing' ? 'Preparing' : eventStatus || 'Update'
                              )
                            ),

                            React.createElement(
                              'p',
                              {
                                className: `text-sm ${statusTextColor} mb-1`,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 2320 },
                              },
                              event.description || event.notes || 'No description'
                            ),

                            hasImage &&
                              React.createElement(
                                'div',
                                {
                                  className:
                                    'mt-2 mb-2 w-full max-w-[200px] h-32 border border-stone-200 rounded-sm overflow-hidden relative group cursor-pointer',
                                  onClick: () => openLightbox(imageUrl),
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 2323 },
                                },

                                React.createElement('img', {
                                  src: imageUrl,
                                  alt: 'Proof',
                                  className:
                                    'w-full h-full object-cover transition-transform duration-500 group-hover:scale-105',
                                  onError: (e) => {
                                    e.target.src = PLACEHOLDER_IMAGE;
                                  },
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 2327 },
                                }),
                                React.createElement(
                                  'div',
                                  {
                                    className:
                                      'absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 2335 },
                                  },
                                  React.createElement(Eye, {
                                    size: 24,
                                    className: 'text-white',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 2336 },
                                  })
                                )
                              ),

                            React.createElement(
                              'div',
                              {
                                className: 'flex gap-3 text-[10px] text-stone-400',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 2341 },
                              },
                              React.createElement(
                                'span',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 2342 },
                                },
                                new Date(event.created_at).toLocaleDateString(),
                                ' • ',
                                new Date(event.created_at).toLocaleTimeString()
                              ),
                              event.location &&
                                event.location !== 'Admin Portal' &&
                                event.location !== 'admin' &&
                                React.createElement(
                                  'span',
                                  {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 2344 },
                                  },
                                  '• ',
                                  event.location
                                )
                            )
                          )
                        );
                      })
                    : React.createElement(
                        'div',
                        {
                          className: 'relative',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 2352 },
                        },
                        React.createElement('div', {
                          className:
                            'absolute -left-[37px] top-1 w-4 h-4 rounded-full bg-stone-300 border-2 border-white',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 2353 },
                        }),
                        React.createElement(
                          'div',
                          {
                            className: 'ml-0',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 2354 },
                          },
                          React.createElement(
                            'p',
                            {
                              className: 'text-sm text-stone-500 italic',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 2355 },
                            },
                            'Order placed. Awaiting updates.'
                          )
                        )
                      )
            )
          ),

          React.createElement(
            'div',
            {
              className: 'bg-stone-50 p-4 border-t border-stone-100 text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2362 },
            },
            React.createElement(
              'p',
              {
                className: 'text-[10px] text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2363 },
              },
              'Estimated Delivery: 3-5 Business Days from Shipment'
            )
          )
        )
      ),

    React.createElement(
      'div',
      {
        className: 'relative h-72 md:h-96 overflow-hidden bg-stone-900 group',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 2369 },
      },
      React.createElement(
        'div',
        {
          className: 'absolute inset-0',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2370 },
        },
        React.createElement('img', {
          src: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop',
          className:
            'w-full h-full object-cover opacity-30 animate-[zoomSlow_30s_infinite_alternate]',
          alt: 'Header Background',
          onError: (e) => {
            e.target.src = PLACEHOLDER_IMAGE;
          },
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2371 },
        }),
        React.createElement('div', {
          className:
            'absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2379 },
        })
      ),

      React.createElement(
        'div',
        {
          className: 'absolute bottom-0 left-0 w-full p-6 md:p-12 max-w-7xl mx-auto flex items-end',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2382 },
        },
        React.createElement(
          'div',
          {
            className: 'flex items-end gap-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 2383 },
          },
          React.createElement(
            'div',
            {
              className:
                'w-28 h-28 md:w-40 md:h-40 rounded-full border-[6px] border-stone-50 bg-stone-200 shadow-2xl overflow-hidden relative group/avatar cursor-pointer',
              onClick: handlePhotoClick,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2384 },
            },

            React.createElement('img', {
              key: profileData.avatar,
              src: profileData.avatar || PLACEHOLDER_IMAGE,
              className:
                'w-full h-full object-cover transition-transform duration-500 group-hover/avatar:scale-110',
              alt: 'Profile',
              onError: (e) => {
                e.target.src = PLACEHOLDER_IMAGE;
              },
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2388 },
            }),
            React.createElement(
              'div',
              {
                className:
                  'absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2397 },
              },
              React.createElement(Camera, {
                className: 'text-white',
                size: 28,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2398 },
              })
            )
          ),
          React.createElement(
            'div',
            {
              className: 'mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2401 },
            },
            React.createElement(
              'h1',
              {
                className: 'font-serif text-3xl md:text-5xl text-white mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2402 },
              },
              profileData.firstName,
              ' ',
              profileData.lastName
            ),
            React.createElement(
              'div',
              {
                className: 'flex flex-wrap items-center gap-3 text-stone-300 text-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2403 },
              },
              React.createElement(
                'span',
                {
                  className:
                    'bg-gold-600 text-white px-3 py-1 rounded text-[10px] uppercase font-bold tracking-widest shadow-lg shadow-gold-900/20',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2404 },
                },
                tierDetails.tierName,
                ' Member'
              ),
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2405 } },
                '•'
              ),
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2406 } },
                'Member since ',
                memberSinceDate
              ),
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2407 } },
                '•'
              ),
              React.createElement(
                'span',
                {
                  className: 'flex items-center gap-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2408 },
                },
                React.createElement(MapPin, {
                  size: 12,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2408 },
                }),
                ' ',
                profileData.city
              )
            )
          )
        )
      )
    ),

    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6 py-12 flex flex-col lg:flex-row gap-12',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 2415 },
      },

      React.createElement(
        'aside',
        {
          className: 'lg:w-64 flex-shrink-0',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2417 },
        },
        React.createElement(
          'nav',
          {
            className: 'space-y-1 sticky top-32',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 2418 },
          },
          [
            {
              id: 'overview',
              label: 'Overview',
              icon: React.createElement(User, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2420 },
              }),
            },
            {
              id: 'orders',
              label: 'Order History',
              icon: React.createElement(Package, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2421 },
              }),
            },
            {
              id: 'payment-proof',
              label: 'Payment Proof',
              icon: React.createElement(Upload, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2422 },
              }),
            },
            {
              id: 'wishlist',
              label: 'My Collection',
              icon: React.createElement(Heart, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2423 },
              }),
            },
            {
              id: 'settings',
              label: 'Settings',
              icon: React.createElement(Settings, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2424 },
              }),
            },
          ].map((item) =>
            React.createElement(
              'button',
              {
                key: item.id,
                onClick: () => setActiveTab(item.id),
                className: `w-full flex items-center justify-between px-5 py-4 rounded-sm transition-all text-sm font-medium border-l-4 ${
                  activeTab === item.id
                    ? 'bg-white text-gold-600 shadow-sm border-gold-600'
                    : 'border-transparent text-stone-500 hover:bg-white hover:text-stone-900'
                }`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2426 },
              },

              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2435 },
                },
                item.icon,
                React.createElement(
                  'span',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2437 } },
                  item.label
                )
              ),
              activeTab === item.id &&
                React.createElement(ChevronRight, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 2439 },
                })
            )
          ),

          React.createElement(
            'div',
            {
              className: 'pt-8 mt-8 border-t border-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 2443 },
            },
            React.createElement(
              'button',
              {
                onClick: onLogout,
                className:
                  'w-full flex items-center gap-3 px-5 py-4 text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors group',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2444 },
              },
              React.createElement(LogOut, {
                size: 18,
                className: 'group-hover:-translate-x-1 transition-transform',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 2445 },
              }),
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 2446 } },
                'Sign Out'
              )
            )
          )
        )
      ),

      React.createElement(
        'main',
        {
          className: 'flex-1 min-h-[500px]',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 2452 },
        },
        renderTabContent()
      )
    )
  );
};

export default MemberProfile;
